import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Routes, Route, useNavigate, useParams, Navigate, Link } from "react-router";
import { App, useRecentView, RequirePolicy, createEmbed } from "@livefree/applications";
import { GoogleAutocomplete, ToggleButtons, ScheduleFrequency, ScheduleBuilder, DrawerActions, ConfirmableButton, Forbidden, withDrawerController, useDrawerController, SaveButton } from "@livefree/react-ui";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useSuccessReactor } from "@livefree/reactive";
import { MqlRequestBuilder, RequestMetadata, dexieRequestBrokerAddon, Sorting, RequestBrokerDataGrid, RequestBroker, Paging } from "@livefree/request-broker";
import { RestClient } from "@livefree/js-sdk";
import { EnvironmentConfigurations, useRuntimeProvider, useComponent } from "@livefree/runtime";
import { createContext, use, useMemo, useState, useCallback, useRef, useEffect, createElement } from "react";
import { Spinner, ListBox, RadioGroup, Radio, cn, Label, Card, Button, Alert, Separator, ProgressBar, Chip, Breadcrumbs, BreadcrumbsItem, Modal, Table, Description } from "@heroui/react";
import { TextField, masks, ToggleButtonsField, SelectField, PhoneField, Uniformly, useForms, EmailField, CheckboxField, TextAreaField } from "@livefree/uniformly";
import { parseAndFormatDate, parseAndFormatDateTime } from "@livefree/common";
import { Filters, FilterSelect, FilterConstant, FilterInput, FilterDateRange, HorizontalFilters, HorizontalChips } from "@livefree/filters";
const reactive = new ReactiveCore("collections");
const PaymentProfileType = {
  CreditCard: 1,
  Ach: 2
};
const PaymentStatusType = {
  Pending: 1,
  Authorized: 2,
  Paid: 3,
  PartiallyRefunded: 4,
  Refunded: 5,
  Voided: 6,
  Declined: 7,
  Error: 9
};
const BankAccountType$1 = {
  Checking: "Checking",
  Savings: "Savings"
};
const isPaymentPending = (status) => status === PaymentStatusType.Pending;
const isPaymentSuccessful = (status) => status === PaymentStatusType.Paid || status === PaymentStatusType.Authorized;
const isPaymentClearing = (status) => status === PaymentStatusType.Authorized;
const isPaymentFailed = (status) => status === PaymentStatusType.Declined || status >= PaymentStatusType.Error;
const PaymentPlanStatus = {
  PendingFirstPayment: 1,
  Active: 2,
  AtRisk: 3,
  Delinquent: 4,
  Completed: 5,
  Canceled: 6
};
const isPaymentPlanTerminal = (statusId) => statusId === PaymentPlanStatus.Completed || statusId === PaymentPlanStatus.Canceled;
const isPaymentPlanSettled = (plan) => plan.amountRemaining <= 0;
let currentToken = null;
const setPaySessionToken = (token) => {
  currentToken = token;
};
const hasPaySession = () => currentToken !== null;
const paySessionTokenLoader = () => Promise.resolve(currentToken);
class CollectionsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "collections");
    this.onRequireToken = paySessionTokenLoader;
  }
  /**
   * `POST /collections/lookup` — identify the customer by exactly one of the three routes.
   *
   * Resolves on a failed lookup as well as a successful one: the API answers 200 with
   * `isSuccessful: false` when nobody matched. Check that flag — a rejection here means a
   * malformed request (400) or a transport/server failure, not "we couldn't find you".
   *
   * Prefer {@link lookupByToken} / {@link lookupByCustomerId} / {@link lookupByNameAndPostalCode}:
   * this signature can express two routes at once, which the API refuses.
   */
  async lookup(request) {
    return await this.httpPOST({
      url: this.appendUrl("/lookup"),
      body: request
    });
  }
  /** Identify by a token the customer arrived holding (typically from a `?token=` link). */
  lookupByToken(token) {
    return this.lookup({ token });
  }
  /** Identify by the customer number off a statement (the customer's account id). */
  lookupByCustomerId(customerId) {
    return this.lookup({ customerId });
  }
  /** Identify by last name plus the postal code on the billing address. */
  lookupByNameAndPostalCode(lastName, postalCode) {
    return this.lookup({ lastName, postalCode });
  }
  /**
   * `GET /collections/orders` — the unpaid orders and total balance for the session's customer.
   *
   * Takes no customer id: the bearer token decides whose orders these are, so there is no id for
   * a caller to substitute. Call it only once a session exists.
   *
   * **Unlike {@link lookup}, this rejects on a bad credential.** An absent, malformed, or expired
   * token is a 401 from the service, surfacing as a rejected promise — never as an empty order
   * list. Treat a rejection as "the session is gone, re-identify", not as "owes nothing"; a
   * resolved response with `orders: []` is the genuine nothing-owed case.
   */
  async getUnpaidOrders() {
    return await this.httpGET({ url: this.appendUrl("/orders") });
  }
  /**
   * `POST /collections/payments` — store the instrument and charge it, for the session's customer.
   *
   * One call, not two: a customer paying online has no stored instrument, so the profile is
   * created as part of taking the payment. Both writes happen server-side and are **not** in one
   * transaction, so a failure between them can leave a stored profile behind with no payment —
   * safe, but it means a retry may store the instrument twice.
   *
   * Rejects like {@link getUnpaidOrders} on a bad session token (401), and with a 400 carrying the
   * validation messages when the request is refused — an amount at or below zero, an unchargeable
   * profile type, an incomplete instrument, or an incomplete billing address.
   *
   * A resolved response means the payment was **accepted and queued**, not that it settled;
   * `statusType` will normally be `Pending`.
   */
  async submitPayment(request) {
    return await this.httpPOST({
      url: this.appendUrl("/payments"),
      body: request
    });
  }
  /**
   * `POST /collections/paymentmethods` — store an instrument without charging it.
   *
   * For the customer who owes nothing but whose card is about to expire. {@link submitPayment} is
   * the only other way an instrument reaches the account from here, and it needs an amount to
   * charge, so a customer with a zero balance has no other route.
   *
   * One call, and it is finished when it resolves — nothing is queued and there is nothing to poll.
   * Storing the instrument also makes it the default, so there is no flag to send.
   *
   * `isDefault` comes back false when the customer has no subscription, which is where the default
   * is recorded. That is not a failure: the instrument is on file, and the call resolves.
   *
   * Rejects on a bad session token (401), and with a 400 carrying the validation messages when the
   * instrument is incomplete or of a type that cannot be charged.
   */
  async savePaymentMethod(request) {
    return await this.httpPOST({
      url: this.appendUrl("/paymentmethods"),
      body: request
    });
  }
  /**
   * `GET /collections/payments/{id}` — the current state of one of the session customer's payments.
   *
   * Poll this after {@link submitPayment}: the charge is queued rather than awaited, so it comes
   * back `Pending` and settles later. `isPaymentPending` is the loop condition.
   *
   * A payment belonging to somebody else answers 404, identically to one that does not exist —
   * telling them apart would let a caller with one valid token discover other customers' payment
   * ids.
   */
  async getPayment(paymentId) {
    return await this.httpGET({
      url: this.appendUrl(`/payments/${paymentId}`)
    });
  }
  /**
   * `POST /collections/payments/confirm` — the things that must only happen once a payment has
   * actually succeeded: the receipt email, and keeping the instrument as the default.
   *
   * Call it **after** polling shows success, never straight after {@link submitPayment} — at
   * creation nothing knows yet whether the charge will clear.
   *
   * Resolves with nothing (204). Rejects 404 if `payment.paymentProfileId` is not one of this
   * customer's profiles, which is the one field on the request the server does not take on trust.
   */
  async confirmPayment(request) {
    await this.httpPOST({ url: this.appendUrl("/payments/confirm"), body: request });
  }
  /**
   * `GET /collections/banks/{routingNumber}` — the bank a routing number belongs to.
   *
   * A convenience, never a gate. `bankName` is `null` when the directory has no entry, which is an
   * ordinary answer rather than an error — the directory does not cover every routing number, and a
   * customer whose bank is missing must still be able to pay by typing the name themselves. Callers
   * should treat a rejection the same way.
   *
   * The routing number is echoed back so a late response can be matched to the value it was asked
   * about, rather than applied to whatever is in the field by the time it lands.
   */
  async lookupBank(routingNumber) {
    return await this.httpGET({
      url: this.appendUrl(`/banks/${encodeURIComponent(routingNumber)}`)
    });
  }
}
class PaymentPlansClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "collections/payment-plans");
  }
  /**
   * `POST /collections/payment-plans` — arrange a plan *and the schedule that charges it*.
   *
   * One call, not two. The API creates the plan, hands `request.schedule` to the Scheduling module
   * stamped as this plan's, and copies its answers back — so the returned plan carries the recurrence
   * sentence and the first charge date **Scheduling** resolved, not the ones sent. Read both off the
   * response.
   *
   * There is no Scheduling client in JS and none is needed here.
   *
   * Rejects with a 400 carrying the validation messages when the plan is refused — including when
   * `schedule` is missing, since a plan without one would never charge.
   */
  async createPlan(request) {
    return await this.create(request);
  }
  /**
   * `POST /collections/payment-plans/{id}/cancel` — the one change a plan takes after setup.
   *
   * Marks the plan Canceled, clears its next charge date and stops the schedule that was charging
   * it, in one call. Gated by `PaymentPlans/Cancel`, a policy of its own rather than `Update`.
   *
   * Rejects with a 404 when there is no such plan, and with a 400 when the plan is already Completed
   * or Canceled — check {@link isPaymentPlanTerminal} before offering it. Also rejects when the
   * schedule could not be stopped: the status change is rolled back with it, so the plan is still
   * cancellable and the call can simply be retried.
   */
  async cancelPlan(id) {
    return await this.httpPOST({ url: this.appendUrl(`/${id}/cancel`) });
  }
  /**
   * `PUT /collections/payment-plans/{id}/payment-profile` — move the plan's future installments onto
   * another of the customer's stored profiles. Gated by `PaymentPlans/Update`.
   *
   * Writes the profile and nothing else, so it cannot overwrite a status that changed since the
   * plan was read. Answers with the plan as it now stands, the new profile's name included.
   *
   * Rejects with a 404 when there is no such plan, and with a 400 when the plan is already Completed
   * or Canceled, or the profile is not the customer's or cannot be charged.
   */
  async changePaymentProfile(id, paymentProfileId) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/payment-profile`),
      body: { paymentProfileId }
    });
  }
  /**
   * The customer's open plans (not Completed or Canceled), oldest first — the ones a payment taken
   * by hand can count toward. Read through `/query` (so `PaymentPlans/View`), the whole customer at
   * once: a customer holds a handful of plans, and the terminal ones are dropped here rather than in
   * the query.
   *
   * Its own action, not `queryMql`, on purpose: the reactor clears the `paymentPlans` store on every
   * `queryMql` answer, and a picker in a payment drawer must not empty a plans list on the same page.
   */
  async getOpenByCustomer(customerId) {
    const result = await this.httpPOST({
      url: this.appendUrl("/query"),
      body: `FETCH PaymentPlanModel MATCH[CustomerId == ${Number(customerId)}];`
    });
    return (result.values ?? []).filter((plan) => !isPaymentPlanTerminal(plan.statusId)).sort((a, b) => a.id - b.id);
  }
  /** `GET /collections/payment-plans/{id}` — one plan. Rejects 404 when there is no such plan. */
  async getById(id) {
    return await this.fetch(id);
  }
  /**
   * `POST /collections/payment-plans/query` — run an MQL query over plans and get back a page of
   * results (`values` plus `totalCount`/`skip`/`take`).
   *
   * There is no get-all route: listing goes through here, which filters and pages.
   *
   * Pass the request-broker `metadata`; it is compiled to an MQL string before the call. The FETCH
   * name is `PaymentPlanModel` — the API resolves it against the AutoMapper map's source type, so it
   * is the *model's* .NET type name, not the entity's and not the swagger schema id
   * (`CollectionsPaymentPlanModel`).
   */
  async queryMql(metadata, _actionKey) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("PaymentPlanModel") : "FETCH PaymentPlanModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query
    });
  }
}
class PaymentPlanStatusTypesClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "collections/payment-plan-status-types");
  }
  /**
   * `GET /collections/payment-plan-status-types` — every status, ordered as a plan moves through
   * them rather than alphabetically.
   *
   * Six seeded rows, so this is a whole-table read with no paging and no filter. Use it for the
   * display text; the ids a caller has to act on are in `PaymentPlanStatus`, which is mirrored
   * rather than read from here.
   */
  async getAll() {
    return await this.get();
  }
}
class PaymentPlanPaymentsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "collections/payment-plan-payments");
  }
  /**
   * `GET /collections/payment-plan-payments?paymentPlanId=` — the plan's ledger, oldest first.
   *
   * A plan with no charges yet answers with an empty list, and so does an unknown plan: the ledger
   * is read without loading the plan, so the two are the same answer. Rejects with a 400 for a
   * non-positive id rather than answering empty.
   */
  async getByPaymentPlan(paymentPlanId) {
    return await this.get({ paymentPlanId: String(paymentPlanId) });
  }
  /**
   * `GET /collections/payment-plan-payments?paymentId=` — the entry recording one payment, as a list
   * of at most one. Empty when the payment is not a plan's, which is the ordinary answer for most
   * payments, so it is a 200 rather than a 404. Rejects with a 400 for a non-positive id.
   */
  async getByPayment(paymentId) {
    return await this.get({ paymentId: String(paymentId) });
  }
  /**
   * `POST /collections/payment-plan-payments` — put a card or ACH payment on a plan's ledger. A
   * payment that already went through (card Paid; ACH Authorized or Paid) is settled at once and the
   * plan moves as it would for a charge; a pending one is settled when the processor answers.
   * Answers with the entry; linking it to the same plan again answers with the same entry.
   *
   * Rejects with a 400 when the plan is closed, the payment is another customer's, not card or ACH,
   * did not go through, is already on another plan, or is for more than the plan still owes; and a
   * 404 when either is unknown. Gated by `PaymentPlans/Update`.
   */
  async link(request) {
    return await this.create(request);
  }
}
const CollectionsConfig = EnvironmentConfigurations.hydrate({
  // ===== Artemis (the authenticated host) =====
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "CollectionsAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "CollectionsAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "CollectionsAPI"
    }
  },
  // ===== Pay (the anonymous customer host) =====
  developmentPay: {
    host: "https://localhost:9004",
    api: {
      host: "https://localhost:7104",
      apiKey: "CollectionsAPI"
    }
  },
  // The Static Web App's generated hostname; the named staging environment inserts `-staging`.
  stagingPay: {
    host: "https://ashy-pond-03f55581e-staging.westus2.7.azurestaticapps.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "CollectionsAPI"
    }
  },
  productionPay: {
    host: "https://pay.alert1.com",
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "CollectionsAPI"
    }
  },
  // Same settings as `productionPay`, reached by the generated hostname rather than the custom
  // domain. Both stay live once the domain is bound, so both need an entry.
  productionPayDefaultHost: {
    host: "https://ashy-pond-03f55581e.7.azurestaticapps.net",
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "CollectionsAPI"
    }
  }
});
const paymentPlansPageKey = (queryMql, customerId) => customerId != null ? `paymentPlans/customer/${customerId}` : queryMql.action;
const PaymentPlansService = reactive.service(
  "paymentPlans",
  PaymentPlansClient
)((Service) => new Service(CollectionsConfig.current?.api?.host ?? ""));
PaymentPlansService.queryMql.latest(([, actionKey]) => actionKey);
const pageKeysOf = (plan) => plan.customerId != null ? [
  paymentPlansPageKey(PaymentPlansService.queryMql),
  paymentPlansPageKey(PaymentPlansService.queryMql, plan.customerId)
] : [paymentPlansPageKey(PaymentPlansService.queryMql)];
PaymentPlansService.reactor("paymentPlansReactor", {
  success: (response, _, db2) => {
    const { source, payload } = response;
    switch (source) {
      case PaymentPlansService.queryMql: {
        const pageKey = response.args?.[1] ?? source.action;
        RequestMetadata.setCount(pageKey, payload.totalCount ?? 0);
        return replaceRows(
          db2,
          db2.paymentPlansPages,
          payload.values?.map((row) => ({ ...row, pageKey })),
          { where: (pages) => pages.where("pageKey").equals(pageKey) }
        );
      }
      case PaymentPlansService.getById:
        return db2.paymentPlans.put(payload);
      // Into the lists it belongs to as well, so they show the new plan without a query.
      case PaymentPlansService.createPlan:
        return Promise.all([
          db2.paymentPlans.put(payload),
          db2.paymentPlansPages.bulkPut(
            pageKeysOf(payload).map((pageKey) => ({ ...payload, pageKey }))
          )
        ]);
      // The changed plan comes back as it now stands, so the detail screen re-renders from the
      // store without a second fetch. Only the rows the lists' pages hold are changed; none is added.
      case PaymentPlansService.cancelPlan:
      case PaymentPlansService.changePaymentProfile:
        return Promise.all([
          db2.paymentPlans.put(payload),
          db2.paymentPlansPages.where("id").equals(payload.id).modify((row) => Object.assign(row, payload))
        ]);
    }
  },
  // Optional-chained, unlike the other apps': this app is also mounted in the pay host, which
  // composes its own provider set, so the alerts service is not guaranteed to be present.
  error: (response) => appRuntime?.services?.alerts?.error?.(response.payload?.message ?? "Something went wrong.")
});
const PaymentPlanPaymentsService = reactive.service(
  "paymentPlanPayments",
  PaymentPlanPaymentsClient
)((Service) => new Service(CollectionsConfig.current?.api?.host ?? ""));
PaymentPlanPaymentsService.reactor("paymentPlanPaymentsReactor", {
  success: (response, _, db2) => {
    const { source, args, payload } = response;
    switch (source) {
      case PaymentPlanPaymentsService.getByPaymentPlan: {
        const paymentPlanId = Number(args[0]);
        return replaceRows(db2, db2.paymentPlanPayments, payload, {
          where: (payments) => payments.where("paymentPlanId").equals(paymentPlanId)
        });
      }
      // At most one entry, merged rather than replaced: it is one row of some plan's ledger, and
      // the rest of that ledger is not in the answer.
      case PaymentPlanPaymentsService.getByPayment:
        return db2.paymentPlanPayments.bulkPut(payload ?? []);
      case PaymentPlanPaymentsService.link:
        appRuntime?.services?.alerts?.success?.(
          `Payment #${payload.paymentId} added to payment plan #${payload.paymentPlanId}.`
        );
        return db2.paymentPlanPayments.put(payload);
    }
  },
  // Optional-chained, as in PaymentPlansService: the pay host composes its own provider set.
  error: (response) => appRuntime?.services?.alerts?.error?.(response.payload?.message ?? "Something went wrong.")
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const db = new DexieDatabaseProvider([dexieRequestBrokerAddon]);
const ReactiveConfig = {
  db: {
    name: "livefree.collections.db",
    provider: db,
    init: (db2) => {
      db2.version(1).stores(storeFormat(_storeV1));
      db2.version(2).stores(storeFormat(_storeV2));
      db2.version(3).stores(storeFormat(_storeV3));
      db2.version(4).stores(storeFormat(_storeV4));
      db2.version(5).stores(storeFormat(_storeV5));
      db2.version(6).stores({ ...storeFormat(_store), paymentPlansPage: null });
    }
  }
};
const _storeV1 = {
  payments: ["&id"]
};
const _storeV2 = {
  payments: ["&id"],
  paymentPlans: ["&id"]
};
const _storeV3 = {
  payments: ["&id"],
  paymentPlans: ["&id"],
  paymentPlanPayments: ["&id", "paymentPlanId"]
};
const _storeV4 = {
  payments: ["&id"],
  paymentPlans: ["&id", "customerId"],
  paymentPlanPayments: ["&id", "paymentPlanId"]
};
const _storeV5 = {
  payments: ["&id"],
  paymentPlans: ["&id", "customerId"],
  paymentPlansPage: ["&id", "customerId"],
  paymentPlanPayments: ["&id", "paymentPlanId"]
};
const _store = {
  // Payments the customer submitted in this session, keyed by the id the API assigns.
  payments: ["&id"],
  // Payment plans read one at a time: whichever plan the detail screen has open. Added in v2 —
  // hence the version bump, or an already-provisioned database would never create the store.
  paymentPlans: ["&id", "customerId"],
  // The current page of each list's query, apart from `paymentPlans` so replacing a page cannot
  // erase the plan the detail screen is showing. One page per list scope: each row carries its
  // list's request-broker key as `pageKey` (see paymentPlansPageKey), so the same plan can be on the
  // full list's page and on its customer's. Indexed by `id` so a change reaches every copy. Added in
  // v6, in place of v5's `paymentPlansPage`.
  paymentPlansPages: ["[pageKey+id]", "pageKey", "id"],
  // A plan's ledger, mirrored whole per plan the detail screen has open. Indexed by plan so the
  // screen can read its rows and the reactor can replace them without touching another plan's.
  // Added in v3, with the same version bump for the same reason.
  paymentPlanPayments: ["&id", "paymentPlanId"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "flex h-full flex-col p-4", children });
let Bootstrapped;
const AppLayout = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const RoutePaths = () => {
  const baseUrl = appRuntime.baseUrl;
  const rootPaths = {
    payment: baseUrl + "/payment",
    paymentPlans: baseUrl + "/payment-plans"
  };
  return {
    payment: {
      root: () => rootPaths.payment,
      confirmation: () => `${rootPaths.payment}/confirmation`
    },
    // No `edit`, `cancel` or a path to remove one: a plan is not edited after setup, and cancelling
    // it is a button on the detail screen rather than a route of its own.
    paymentPlans: {
      list: () => rootPaths.paymentPlans,
      setup: () => `${rootPaths.paymentPlans}/setup`,
      details: (id) => `${rootPaths.paymentPlans}/${id}/details`
    }
  };
};
const PaySessionContext = createContext(null);
const useHasPaySession = () => use(PaySessionContext) !== null;
const PaySessionProvider = ({
  session,
  onEndSession,
  children
}) => {
  setPaySessionToken(session.token);
  const value = useMemo(
    () => ({ ...session, endSession: onEndSession }),
    [session, onEndSession]
  );
  return /* @__PURE__ */ jsx(PaySessionContext, { value, children });
};
const usePaySession = () => {
  const session = use(PaySessionContext);
  if (!session) {
    throw new Error(
      "usePaySession must be used within a PaySessionProvider. The host establishes the customer identity and renders <PaySessionProvider> around this app."
    );
  }
  return session;
};
const POLL_INTERVAL_MS = 2e3;
const POLL_TIMEOUT_MS = 6e4;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const awaitSettlement = async (client2, paymentId, { signal } = {}) => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let latest = null;
  for (; ; ) {
    if (signal?.cancelled) return latest;
    latest = await client2.getPayment(paymentId);
    if (!isPaymentPending(latest.statusType)) return latest;
    if (Date.now() + POLL_INTERVAL_MS >= deadline) return latest;
    await delay(POLL_INTERVAL_MS);
  }
};
const AmountMode = {
  Full: "full",
  Other: "other"
};
const PaymentMethodType = {
  Card: "card",
  BankAccount: "bank"
};
const BankAccountType = {
  Checking: "checking",
  Savings: "savings"
};
const BANK_ACCOUNT_TYPE_BUTTONS = [
  { key: BankAccountType.Checking, label: "Checking" },
  { key: BankAccountType.Savings, label: "Savings" }
];
const digitsOnly$1 = (value) => (value ?? "").replace(/\D/g, "");
const splitName = (full) => {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: "", lastName: parts[0] };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
};
const splitExpiration = (expiration) => {
  const digits = digitsOnly$1(expiration);
  if (digits.length !== 4) return { expirationMonth: "", expirationYear: "" };
  const century = Math.floor((/* @__PURE__ */ new Date()).getFullYear() / 100) * 100;
  return {
    expirationMonth: digits.slice(0, 2),
    expirationYear: String(century + Number(digits.slice(2)))
  };
};
const timestamp = () => {
  const now = /* @__PURE__ */ new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};
const profileName = (label, number) => {
  const last4 = digitsOnly$1(number).slice(-4);
  const base = last4 ? `${label} ending ${last4}` : label;
  return `${base} · ${timestamp()}`;
};
const toProfileRequest = ({
  methodType,
  method,
  receiptEmail,
  billingPhone,
  billingAddress
}) => {
  const isCard = methodType === PaymentMethodType.Card;
  const { firstName, lastName } = splitName(
    isCard ? method.cardholderName : method.bankAccountHolderName
  );
  const profile = isCard ? {
    profileType: PaymentProfileType.CreditCard,
    name: profileName("Card", method.cardNumber),
    cardNumber: digitsOnly$1(method.cardNumber),
    ...splitExpiration(method.expiration),
    cvv: digitsOnly$1(method.cvv),
    firstName,
    lastName
  } : {
    profileType: PaymentProfileType.Ach,
    name: profileName(
      method.bankAccountType === BankAccountType$1.Savings ? "Savings" : "Checking",
      method.bankAccountNumber
    ),
    routingNumber: digitsOnly$1(method.routingNumber),
    // Required, not optional: Transactions' bank profile validator and PaySimple's own account
    // validator both refuse a profile with no bank name, so omitting it fails at the sync rather
    // than here.
    bankName: method.bankName,
    accountNumber: digitsOnly$1(method.bankAccountNumber),
    accountType: method.bankAccountType ?? BankAccountType$1.Checking,
    firstName,
    lastName
  };
  if (receiptEmail) profile.email = receiptEmail;
  if (billingPhone) profile.phoneNumber = billingPhone;
  return {
    profile,
    billingAddress: {
      address1: billingAddress.address1 ?? "",
      ...billingAddress.address2 ? { address2: billingAddress.address2 } : {},
      city: billingAddress.city ?? "",
      state: (billingAddress.state ?? "").toUpperCase(),
      postalCode: billingAddress.postalCode ?? "",
      // Defaulted rather than assumed: the field is always present, but the Google lookup can
      // leave it empty for a result with no country component, and the API requires a value.
      country: billingAddress.country || "US"
    }
  };
};
const toPaymentRequest = (submitted) => ({
  ...toProfileRequest(submitted),
  amount: submitted.amount
});
const digitsOnly = (value) => (value ?? "").replace(/\D/g, "");
const isExpiryInFuture = (value) => {
  const match = /^(\d{2})\/(\d{2})$/.exec(value ?? "");
  if (!match) return false;
  const month = Number(match[1]);
  if (month < 1 || month > 12) return false;
  const now = /* @__PURE__ */ new Date();
  const expiresAfter = new Date(2e3 + Number(match[2]), month, 1);
  return expiresAfter > now;
};
const CardFields = ({ initial = {} }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
  /* @__PURE__ */ jsx(
    TextField,
    {
      name: "cardNumber",
      label: "Card number",
      value: initial.cardNumber ?? "",
      mask: masks.cardNumberMask,
      placeholder: "1234 5678 9012 3456",
      inputMode: "numeric",
      autoComplete: "cc-number",
      isRequired: true,
      validation: (x) => x.string().required("Card number is required").test("length", "Enter a valid card number", (value) => {
        const digits = digitsOnly(value);
        return digits.length >= 13 && digits.length <= 19;
      })
    }
  ),
  /* @__PURE__ */ jsx(
    TextField,
    {
      name: "cardholderName",
      label: "Cardholder name",
      value: initial.cardholderName ?? "",
      placeholder: "Name as it appears on the card",
      autoComplete: "cc-name",
      isRequired: true,
      validation: (x) => x.string().required("Cardholder name is required")
    }
  ),
  /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "expiration",
        label: "Expiration",
        value: initial.expiration ?? "",
        mask: masks.cardDateMask,
        placeholder: "MM / YY",
        inputMode: "numeric",
        autoComplete: "cc-exp",
        isRequired: true,
        validation: (x) => x.string().required("Expiration is required").test("format", "Use MM / YY", (value) => /^(0[1-9]|1[0-2])\/\d{2}$/.test(value ?? "")).test("future", "That card has expired", isExpiryInFuture)
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "cvv",
        value: initial.cvv ?? "",
        mask: "0000",
        label: "CVV",
        placeholder: "123",
        inputMode: "numeric",
        autoComplete: "cc-csc",
        isRequired: true,
        validation: (x) => x.string().required("CVV is required").matches(/^\d{3,4}$/, "Enter the 3 or 4 digit code")
      }
    )
  ] })
] });
const BankAccountFields = ({ initial = {}, bankName = null, isLookingUpBank = false }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
  /* @__PURE__ */ jsx(
    TextField,
    {
      name: "routingNumber",
      label: "Routing number",
      value: initial.routingNumber ?? "",
      mask: /^\d{0,9}$/,
      placeholder: "123456789",
      inputMode: "numeric",
      description: "9-digit number on the bottom-left of a check.",
      isRequired: true,
      validation: (x) => x.string().required("Routing number is required").matches(/^\d{9}$/, "A routing number is 9 digits")
    }
  ),
  /* @__PURE__ */ jsx(
    TextField,
    {
      name: "bankName",
      label: "Bank name",
      value: bankName ?? initial.bankName ?? "",
      placeholder: "Name of your bank",
      description: isLookingUpBank ? "Looking up your bank…" : "Filled in from your routing number when we can.",
      endContent: isLookingUpBank ? /* @__PURE__ */ jsx(Spinner, { size: "sm", "aria-label": "Looking up your bank" }) : null,
      isRequired: true,
      validation: (x) => x.string().required("Bank name is required")
    }
  ),
  /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
    /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Account type" }),
    /* @__PURE__ */ jsx(
      ToggleButtonsField,
      {
        name: "bankAccountType",
        value: initial.bankAccountType ?? BankAccountType.Checking,
        initialValue: initial.bankAccountType ?? BankAccountType.Checking,
        buttons: BANK_ACCOUNT_TYPE_BUTTONS
      }
    )
  ] }),
  /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "bankAccountNumber",
        label: "Account number",
        value: initial.bankAccountNumber ?? "",
        mask: /^\d{0,17}$/,
        placeholder: "000000000",
        inputMode: "numeric",
        isRequired: true,
        validation: (x) => x.string().required("Account number is required").matches(/^\d{4,17}$/, "Enter a valid account number")
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "bankAccountHolderName",
        label: "Account holder name",
        value: initial.bankAccountHolderName ?? "",
        placeholder: "Name on the account",
        isRequired: true,
        validation: (x) => x.string().required("Account holder name is required")
      }
    )
  ] }),
  /* @__PURE__ */ jsxs("p", { className: "text-muted bg-surface-secondary flex items-start gap-2 rounded-md p-3 text-xs", children: [
    /* @__PURE__ */ jsx("i", { className: "ri-information-line mt-px", "aria-hidden": "true" }),
    /* @__PURE__ */ jsxs("span", { children: [
      "Bank transfers post immediately but can take up to ",
      /* @__PURE__ */ jsx("strong", { children: "5 business days" }),
      " to fully clear."
    ] })
  ] })
] });
const COUNTRIES = [
  { id: "US", name: "United States" },
  { id: "CA", name: "Canada" },
  { id: "MX", name: "Mexico" }
];
const BillingAddressFields = () => {
  const [picked, setPicked] = useState({});
  const handleSelect = (selected) => setPicked({
    address1: selected.address1?.trim() ?? "",
    address2: "",
    city: selected.city ?? "",
    state: selected.state ?? "",
    postalCode: selected.postalCode ?? "",
    country: selected.country || "US"
  });
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Billing details" }),
    /* @__PURE__ */ jsx(
      GoogleAutocomplete,
      {
        onSelect: handleSelect,
        placeholder: "Start typing your address…",
        startContent: /* @__PURE__ */ jsx("i", { className: "ri-map-pin-line text-base" })
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "address1",
        label: "Street address",
        value: picked.address1 ?? "",
        placeholder: "123 Main St",
        autoComplete: "billing address-line1",
        isRequired: true,
        validation: (x) => x.string().required("Street address is required")
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "address2",
        label: "Apt, suite, etc. (optional)",
        value: picked.address2 ?? "",
        placeholder: "Apt 4B",
        autoComplete: "billing address-line2"
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "city",
          label: "City",
          value: picked.city ?? "",
          placeholder: "City",
          autoComplete: "billing address-level2",
          isRequired: true,
          validation: (x) => x.string().required("City is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "state",
          label: "State",
          value: picked.state ?? "",
          placeholder: "PA",
          transform: "upper",
          autoComplete: "billing address-level1",
          isRequired: true,
          validation: (x) => x.string().required("State is required").matches(/^[A-Za-z]{2}$/, "Use the 2-letter state code")
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "postalCode",
          label: "ZIP code",
          value: picked.postalCode ?? "",
          placeholder: "12345",
          inputMode: "numeric",
          autoComplete: "billing postal-code",
          isRequired: true,
          validation: (x) => x.string().required("ZIP code is required").matches(/^\d{5}(-\d{4})?$/, "Enter a valid ZIP code")
        }
      ),
      /* @__PURE__ */ jsx(
        SelectField,
        {
          name: "country",
          label: "Country",
          value: picked.country ?? "US",
          items: COUNTRIES,
          isRequired: true,
          children: (item) => /* @__PURE__ */ jsx(ListBox.Item, { id: item.id, children: item.name }, item.id)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex max-w-xs flex-col gap-1.5", children: [
      /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium", children: [
        "Billing phone ",
        /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
      ] }),
      /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "billingPhone",
          "aria-label": "Billing phone",
          isRequired: true,
          validation: (x) => x.string().required("Billing phone is required").min(12, "Enter a valid phone number")
        }
      )
    ] })
  ] });
};
const METHODS = [
  {
    value: PaymentMethodType.Card,
    icon: "ri-bank-card-line",
    title: "Credit or debit card",
    description: "Processed immediately"
  },
  {
    value: PaymentMethodType.BankAccount,
    icon: "ri-bank-line",
    title: "Bank account (ACH)",
    description: "Posts now, clears in ~5 business days"
  }
];
const PaymentMethodSelector = ({ value, onChange }) => /* @__PURE__ */ jsx(
  RadioGroup,
  {
    value,
    onChange,
    orientation: "horizontal",
    "aria-label": "Payment method",
    className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
    children: METHODS.map((method) => /* @__PURE__ */ jsx(
      Radio,
      {
        value: method.value,
        className: cn(
          "bg-surface m-0 cursor-pointer items-center gap-3 rounded-lg border p-3",
          "border-separator hover:bg-surface-secondary transition-colors",
          "data-[selected=true]:border-accent data-[selected=true]:bg-accent/5"
        ),
        children: /* @__PURE__ */ jsxs(Radio.Content, { className: "flex w-full items-center gap-3", children: [
          /* @__PURE__ */ jsx("span", { className: "bg-surface-secondary flex h-7 w-7 shrink-0 items-center justify-center rounded", children: /* @__PURE__ */ jsx("i", { className: cn(method.icon, "text-sm"), "aria-hidden": "true" }) }),
          /* @__PURE__ */ jsxs("span", { className: "flex min-w-0 flex-col", children: [
            /* @__PURE__ */ jsx(Label, { className: "cursor-pointer text-sm font-semibold", children: method.title }),
            /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: method.description })
          ] })
        ] })
      },
      method.value
    ))
  }
);
const PaymentMethodSection = ({
  methodType,
  onChangeMethodType,
  preserved,
  bankLookup,
  onMethodChange,
  onAddressChange
}) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx(PaymentMethodSelector, { value: methodType, onChange: onChangeMethodType }),
  /* @__PURE__ */ jsx(Uniformly, { renderForm: false, onChange: onMethodChange, throttle: 100, children: methodType === PaymentMethodType.Card ? /* @__PURE__ */ jsx(CardFields, { initial: preserved }) : /* @__PURE__ */ jsx(
    BankAccountFields,
    {
      initial: preserved,
      bankName: bankLookup.bankName,
      isLookingUpBank: bankLookup.isLooking
    }
  ) }, methodType),
  /* @__PURE__ */ jsx(Uniformly, { renderForm: false, onChange: onAddressChange, throttle: 100, children: /* @__PURE__ */ jsx(BillingAddressFields, {}) })
] });
const usePaymentMethod = ({ forms, handleFormChange, onLookupBank }) => {
  const [methodType, setMethodType] = useState(PaymentMethodType.Card);
  const onMethodChange = useCallback((f) => handleFormChange("method", f), [handleFormChange]);
  const onAddressChange = useCallback((f) => handleFormChange("address", f), [handleFormChange]);
  const preservedMethod = useRef({});
  const changeMethodType = useCallback(
    (next) => {
      preservedMethod.current = { ...preservedMethod.current, ...forms.data };
      setMethodType(next);
    },
    [forms]
  );
  const typedRoutingNumber = methodType === PaymentMethodType.BankAccount ? forms.rawData.routingNumber ?? "" : "";
  const [bankLookup, setBankLookup] = useState({ isLooking: false, bankName: null });
  useEffect(() => {
    const digits = String(typedRoutingNumber).replace(/\D/g, "");
    if (digits.length !== 9) {
      setBankLookup({ isLooking: false, bankName: null });
      return void 0;
    }
    let cancelled = false;
    setBankLookup({ isLooking: true, bankName: null });
    onLookupBank(digits).then((result) => {
      if (!cancelled) setBankLookup({ isLooking: false, bankName: result?.bankName ?? null });
    }).catch(() => {
      if (!cancelled) setBankLookup({ isLooking: false, bankName: null });
    });
    return () => {
      cancelled = true;
    };
  }, [typedRoutingNumber, onLookupBank]);
  const readSubmission = useCallback(
    (raw) => ({
      methodType,
      method: methodType === PaymentMethodType.Card ? {
        cardNumber: raw.cardNumber,
        cardholderName: raw.cardholderName,
        expiration: raw.expiration,
        cvv: raw.cvv,
        billingPostalCode: raw.billingPostalCode
      } : {
        routingNumber: raw.routingNumber,
        bankName: raw.bankName,
        bankAccountNumber: raw.bankAccountNumber,
        bankAccountHolderName: raw.bankAccountHolderName,
        bankAccountType: raw.bankAccountType
      },
      billingAddress: {
        address1: raw.address1,
        address2: raw.address2,
        city: raw.city,
        state: raw.state,
        postalCode: raw.postalCode,
        country: raw.country
      },
      // Alongside the address rather than inside it: the API hangs the phone off the payment
      // profile, and AddressModel has no field for it.
      billingPhone: raw.billingPhone
    }),
    [methodType]
  );
  return {
    methodType,
    changeMethodType,
    preserved: preservedMethod.current,
    bankLookup,
    onMethodChange,
    onAddressChange,
    readSubmission
  };
};
const PaymentMethodForm = ({ isSaving, saveError, onSave, onCancel, onLookupBank }) => {
  const [forms, handleFormChange] = useForms();
  const paymentMethod = usePaymentMethod({ forms, handleFormChange, onLookupBank });
  const canSubmit = forms.isValid && !isSaving;
  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSave(paymentMethod.readSubmission(forms.rawData));
  };
  return /* @__PURE__ */ jsxs(Card, { className: "w-full", children: [
    /* @__PURE__ */ jsxs(Card.Header, { children: [
      /* @__PURE__ */ jsx(Card.Title, { children: "Update your payment method" }),
      /* @__PURE__ */ jsx(Card.Description, { children: "We'll use this for your future bills on this account. Nothing is charged now." })
    ] }),
    /* @__PURE__ */ jsx(Card.Content, { className: "p-6 pt-0", children: /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, className: "flex flex-col gap-6", noValidate: true, children: [
      /* @__PURE__ */ jsx("section", { className: "flex flex-col gap-3", children: /* @__PURE__ */ jsx(
        PaymentMethodSection,
        {
          methodType: paymentMethod.methodType,
          onChangeMethodType: paymentMethod.changeMethodType,
          preserved: paymentMethod.preserved,
          bankLookup: paymentMethod.bankLookup,
          onMethodChange: paymentMethod.onMethodChange,
          onAddressChange: paymentMethod.onAddressChange
        }
      ) }),
      saveError && /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
        /* @__PURE__ */ jsx(Alert.Indicator, {}),
        /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsx(Alert.Title, { children: saveError }) })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
        /* @__PURE__ */ jsxs(
          Button,
          {
            type: "submit",
            variant: "primary",
            isDisabled: !canSubmit,
            isPending: isSaving,
            fullWidth: true,
            children: [
              isSaving && /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }),
              /* @__PURE__ */ jsx("i", { className: "ri-lock-line", "aria-hidden": "true" }),
              "Save payment method"
            ]
          }
        ),
        /* @__PURE__ */ jsx(Button, { variant: "ghost", onPress: onCancel, isDisabled: isSaving, fullWidth: true, children: "Cancel" }),
        /* @__PURE__ */ jsxs("p", { className: "text-muted flex items-center justify-center gap-1.5 text-center text-xs", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-shield-check-line", "aria-hidden": "true" }),
          "Your details are encrypted. Alert1 will never ask for your account password on this page."
        ] })
      ] })
    ] }) })
  ] });
};
const PaymentMethodSavedView = ({ paymentMethod, onDone }) => /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col items-center gap-3 p-8 text-center", children: [
  /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill text-success text-4xl", "aria-hidden": "true" }),
  /* @__PURE__ */ jsx("h1", { className: "text-xl font-semibold", children: "Payment method saved" }),
  /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: paymentMethod.isDefault ? /* @__PURE__ */ jsxs(Fragment, { children: [
    "We'll use ",
    paymentMethod.name,
    " for your future bills on this account."
  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    "We've saved ",
    paymentMethod.name,
    ", but we could not set it as your default. Please contact customer service so they can finish setting it up."
  ] }) }),
  /* @__PURE__ */ jsx(Button, { className: "mt-4", variant: "primary", onPress: onDone, children: "Done" })
] }) });
const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});
const formatCurrency = (amount) => formatter.format(Number.isFinite(amount) ? amount : 0);
const parseCurrency = (input) => {
  if (typeof input === "number") return Number.isFinite(input) ? input : null;
  if (typeof input !== "string") return null;
  const cleaned = input.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};
const toCents = (amount) => Math.round((Number(amount) + Number.EPSILON) * 100);
const AccountSummary = ({ customer, totalBalanceDue, orderCount, hasBalance }) => /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-4 p-6", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsxs("span", { className: "text-muted text-xs", children: [
      "Account ",
      customer.customerId
    ] }),
    /* @__PURE__ */ jsx("h1", { className: "text-2xl font-semibold", children: customer.displayName ? `Hello, ${customer.displayName}` : "Hello" }),
    /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: hasBalance ? "You have a balance on your Alert1 account. You can pay the full amount or a portion of it below." : "Your account is up to date." })
  ] }),
  /* @__PURE__ */ jsxs("div", { className: "bg-surface-secondary border-separator flex items-center justify-between gap-4 rounded-lg border p-4", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted text-xs font-semibold tracking-wide uppercase", children: "Amount Due" }),
      hasBalance && /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: orderCount === 1 ? "Across 1 order" : `Across ${orderCount} orders` })
    ] }),
    /* @__PURE__ */ jsx("span", { className: "text-3xl font-semibold tabular-nums", children: formatCurrency(totalBalanceDue) })
  ] })
] }) });
const OrdersSummary = ({ orders, totalBalanceDue }) => /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col p-6", children: [
  /* @__PURE__ */ jsx("span", { className: "text-muted mb-3 text-xs font-semibold tracking-wide uppercase", children: "Orders with a balance" }),
  /* @__PURE__ */ jsx("ul", { className: "flex flex-col", children: orders.map((order) => /* @__PURE__ */ jsxs(
    "li",
    {
      className: "border-separator flex items-center justify-between gap-4 border-b py-3 last:border-b-0",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
          /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium", children: [
            "Order ",
            order.orderId
          ] }),
          /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: parseAndFormatDate(order.orderDate) })
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-sm font-medium tabular-nums", children: formatCurrency(order.balanceDue) })
      ]
    },
    order.orderId
  )) }),
  /* @__PURE__ */ jsx(Separator, { className: "my-3" }),
  /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4", children: [
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: "Total" }),
    /* @__PURE__ */ jsx("span", { className: "text-base font-semibold tabular-nums", children: formatCurrency(totalBalanceDue) })
  ] })
] }) });
const SectionHeading = ({ step, children }) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
  /* @__PURE__ */ jsx(
    "span",
    {
      "aria-hidden": "true",
      className: "bg-accent text-accent-foreground flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
      children: step
    }
  ),
  /* @__PURE__ */ jsx("h2", { className: "text-muted text-xs font-semibold tracking-wide uppercase", children })
] });
const PaymentForm = ({
  totalBalanceDue,
  isSubmitting,
  submitError,
  onSubmit,
  onLookupBank
}) => {
  const [amountMode, setAmountMode] = useState(AmountMode.Full);
  const [forms, handleFormChange] = useForms();
  const onAmountChange = useCallback((f) => handleFormChange("amount", f), [handleFormChange]);
  const onConfirmChange = useCallback((f) => handleFormChange("confirmation", f), [handleFormChange]);
  const paymentMethod = usePaymentMethod({ forms, handleFormChange, onLookupBank });
  const typedAmount = parseCurrency(forms.data.amount);
  const amount = amountMode === AmountMode.Full ? totalBalanceDue : typedAmount ?? 0;
  const exceedsBalance = toCents(amount) > toCents(totalBalanceDue);
  const canSubmit = forms.isValid && toCents(amount) > 0 && !exceedsBalance && !isSubmitting;
  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const raw = forms.rawData;
    onSubmit({
      ...paymentMethod.readSubmission(raw),
      amount,
      receiptEmail: raw.email || null,
      saveAsDefault: Boolean(raw.saveAsDefault)
    });
  };
  return /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-6", children: /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, className: "flex flex-col gap-6", noValidate: true, children: [
    /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsx(SectionHeading, { step: "1", children: "Amount to pay" }),
      /* @__PURE__ */ jsx(
        ToggleButtons,
        {
          initialValue: amountMode,
          onChange: setAmountMode,
          buttons: [
            {
              key: AmountMode.Full,
              label: `Pay full balance · ${formatCurrency(totalBalanceDue)}`
            },
            { key: AmountMode.Other, label: "Pay a different amount" }
          ]
        }
      ),
      /* @__PURE__ */ jsx(Uniformly, { renderForm: false, onChange: onAmountChange, throttle: 100, children: amountMode === AmountMode.Other && /* @__PURE__ */ jsx("div", { className: "max-w-xs", children: /* @__PURE__ */ jsx(
        TextField,
        {
          name: "amount",
          "aria-label": "Amount to pay",
          placeholder: "0.00",
          inputMode: "decimal",
          startContent: "$",
          description: `Any amount up to ${formatCurrency(totalBalanceDue)}.`,
          isRequired: true,
          validation: (x) => x.string().required("Enter an amount").test("positive", "Enter an amount greater than zero", (value) => {
            const parsed = parseCurrency(value);
            return parsed !== null && toCents(parsed) > 0;
          }).test(
            "within-balance",
            `Enter ${formatCurrency(totalBalanceDue)} or less`,
            (value) => {
              const parsed = parseCurrency(value);
              return parsed !== null && toCents(parsed) <= toCents(totalBalanceDue);
            }
          )
        }
      ) }) }, amountMode)
    ] }),
    /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsx(SectionHeading, { step: "2", children: "Payment method" }),
      /* @__PURE__ */ jsx(
        PaymentMethodSection,
        {
          methodType: paymentMethod.methodType,
          onChangeMethodType: paymentMethod.changeMethodType,
          preserved: paymentMethod.preserved,
          bankLookup: paymentMethod.bankLookup,
          onMethodChange: paymentMethod.onMethodChange,
          onAddressChange: paymentMethod.onAddressChange
        }
      )
    ] }),
    /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsx(SectionHeading, { step: "3", children: "Confirmation" }),
      /* @__PURE__ */ jsxs(Uniformly, { renderForm: false, onChange: onConfirmChange, throttle: 100, children: [
        /* @__PURE__ */ jsx(
          EmailField,
          {
            name: "email",
            label: "Email for receipt",
            placeholder: "you@example.com",
            description: "Optional — we'll send your payment confirmation here."
          }
        ),
        /* @__PURE__ */ jsx(CheckboxField, { name: "saveAsDefault", value: true, children: /* @__PURE__ */ jsxs("span", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm", children: "Use this payment method as my default going forward" }),
          /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "We'll securely save it and use it for future bills on this account." })
        ] }) })
      ] })
    ] }),
    submitError && /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
      /* @__PURE__ */ jsx(Alert.Indicator, {}),
      /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsx(Alert.Title, { children: submitError }) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsxs(
        Button,
        {
          type: "submit",
          variant: "primary",
          isDisabled: !canSubmit,
          isPending: isSubmitting,
          fullWidth: true,
          children: [
            isSubmitting && /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }),
            /* @__PURE__ */ jsx("i", { className: "ri-lock-line", "aria-hidden": "true" }),
            "Pay ",
            formatCurrency(amount)
          ]
        }
      ),
      /* @__PURE__ */ jsxs("p", { className: "text-muted flex items-center justify-center gap-1.5 text-center text-xs", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-shield-check-line", "aria-hidden": "true" }),
        "Payments are encrypted. Alert1 will never ask for your account password on this page. Information provided may be used for this and future collection efforts."
      ] })
    ] })
  ] }) }) });
};
const PaymentView = ({
  customer,
  totalBalanceDue,
  orders,
  isSubmitting,
  submitError,
  isSavingMethod,
  saveMethodError,
  onSubmit,
  onSaveMethod,
  onLookupBank
}) => {
  const hasBalance = orders.length > 0 && totalBalanceDue > 0;
  const [isUpdatingMethod, setIsUpdatingMethod] = useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "flex w-full flex-col items-center gap-4", children: [
    /* @__PURE__ */ jsxs("p", { className: "text-muted flex items-center gap-1.5 text-xs", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-lock-line", "aria-hidden": "true" }),
      "Secure payment"
    ] }),
    /* @__PURE__ */ jsx(
      AccountSummary,
      {
        customer,
        totalBalanceDue,
        orderCount: orders.length,
        hasBalance
      }
    ),
    hasBalance ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(OrdersSummary, { orders, totalBalanceDue }),
      /* @__PURE__ */ jsx(
        PaymentForm,
        {
          totalBalanceDue,
          isSubmitting,
          submitError,
          onSubmit,
          onLookupBank
        }
      )
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col items-center gap-2 p-8 text-center", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-line text-success text-3xl", "aria-hidden": "true" }),
        /* @__PURE__ */ jsx("p", { className: "text-sm font-medium", children: "You're all paid up" }),
        /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: "There is nothing due on your account right now. Thank you." }),
        !isUpdatingMethod && /* @__PURE__ */ jsxs(
          Button,
          {
            className: "mt-2",
            variant: "secondary",
            onPress: () => setIsUpdatingMethod(true),
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-bank-card-line", "aria-hidden": "true" }),
              "Update payment method"
            ]
          }
        )
      ] }) }),
      isUpdatingMethod && /* @__PURE__ */ jsx(
        PaymentMethodForm,
        {
          isSaving: isSavingMethod,
          saveError: saveMethodError,
          onSave: onSaveMethod,
          onCancel: () => setIsUpdatingMethod(false),
          onLookupBank
        }
      )
    ] })
  ] });
};
const PaymentLoadingView = () => /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex items-center justify-center gap-3 p-10", children: [
  /* @__PURE__ */ jsx("i", { className: "ri-loader-4-line animate-spin text-lg", "aria-hidden": "true" }),
  /* @__PURE__ */ jsx("span", { className: "text-muted text-sm", children: "Loading your balance…" })
] }) });
const PaymentReceiptView = ({ customer, payment }) => {
  const { endSession } = usePaySession();
  const succeeded = isPaymentSuccessful(payment.statusType);
  const clearing = isPaymentClearing(payment.statusType);
  const { icon, heading, detail } = clearing ? {
    icon: "ri-checkbox-circle-fill text-success text-4xl",
    heading: "Payment received",
    detail: "Thank you. Bank payments can take up to 5 business days to clear — there is no need to pay again."
  } : succeeded ? {
    icon: "ri-checkbox-circle-fill text-success text-4xl",
    heading: "Payment complete",
    detail: "Your payment was successful."
  } : {
    icon: "ri-time-line text-warning text-4xl",
    heading: "Payment in progress",
    detail: "Your payment is still being processed. There is no need to pay again."
  };
  return /* @__PURE__ */ jsx("div", { className: "flex w-full flex-col items-center gap-4", children: /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col items-center gap-3 p-8 text-center", children: [
    /* @__PURE__ */ jsx("i", { className: icon, "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("h1", { className: "text-xl font-semibold", children: heading }),
    /* @__PURE__ */ jsx("p", { className: "text-3xl font-semibold tabular-nums", children: formatCurrency(payment.amount) }),
    /* @__PURE__ */ jsx("p", { className: "text-muted text-sm", children: detail }),
    /* @__PURE__ */ jsxs("dl", { className: "border-separator mt-2 flex w-full max-w-xs flex-col gap-1 border-t pt-3 text-sm", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx("dt", { className: "text-muted", children: "Confirmation" }),
        /* @__PURE__ */ jsx("dd", { className: "font-medium tabular-nums", children: payment.paymentId })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx("dt", { className: "text-muted", children: "Date" }),
        /* @__PURE__ */ jsx("dd", { className: "font-medium", children: parseAndFormatDateTime(payment.paymentDateUtc) })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx("dt", { className: "text-muted", children: "Account" }),
        /* @__PURE__ */ jsx("dd", { className: "font-medium tabular-nums", children: customer.customerId })
      ] })
    ] }),
    /* @__PURE__ */ jsx(Button, { className: "mt-4", variant: "primary", onPress: endSession, children: "Continue" })
  ] }) }) });
};
const PaymentErrorView = ({ message }) => /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
  /* @__PURE__ */ jsx(Alert.Indicator, {}),
  /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsx(Alert.Title, { children: message }) })
] });
let client;
const getClient = () => client ??= new CollectionsClient(appRuntime.config?.api?.host ?? "");
const LOAD_FAILED_MESSAGE = "We could not load your balance just now. Please refresh the page to try again.";
const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please look up your account again to continue.";
const SUBMIT_FAILED_MESSAGE = "We could not process your payment. Please check your details and try again.";
const SUBMIT_SESSION_MESSAGE = "Your session expired before the payment went through. Please look up your account again.";
const DECLINED_MESSAGE = "Your payment was declined. Please check your details or try a different payment method.";
const SAVE_METHOD_FAILED_MESSAGE = "We could not save your payment method. Please check your details and try again.";
const readValidationMessage = (error, fallback = SUBMIT_FAILED_MESSAGE) => {
  const body = error?.body;
  const errors = body?.validationErrors ?? body?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(" ");
  if (typeof body?.detail === "string" && body.detail) return body.detail;
  return fallback;
};
const PaymentController = () => {
  const { customer, endSession } = usePaySession();
  const [balance, setBalance] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [isSavingMethod, setIsSavingMethod] = useState(false);
  const [saveMethodError, setSaveMethodError] = useState(null);
  const [savedMethod, setSavedMethod] = useState(null);
  const cancellation = useRef({ cancelled: false });
  useEffect(
    () => () => {
      cancellation.current.cancelled = true;
    },
    []
  );
  useEffect(() => {
    let cancelled = false;
    getClient().getUnpaidOrders().then((result) => {
      if (cancelled) return;
      setBalance(result);
    }).catch((error) => {
      if (cancelled) return;
      setLoadError(error?.statusCode === 401 ? SESSION_EXPIRED_MESSAGE : LOAD_FAILED_MESSAGE);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const handleSubmit = useCallback(async (submitted) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const created = await getClient().submitPayment(toPaymentRequest(submitted));
      const settled = await awaitSettlement(getClient(), created.paymentId, {
        signal: cancellation.current
      });
      if (cancellation.current.cancelled) return;
      if (isPaymentFailed(settled.statusType)) {
        setSubmitError(DECLINED_MESSAGE);
        return;
      }
      if (isPaymentSuccessful(settled.statusType)) {
        try {
          await getClient().confirmPayment({
            payment: settled,
            email: submitted.receiptEmail,
            useAsDefault: submitted.saveAsDefault
          });
        } catch {
        }
      }
      setReceipt(settled);
    } catch (error) {
      setSubmitError(
        error?.statusCode === 401 ? SUBMIT_SESSION_MESSAGE : readValidationMessage(error)
      );
    } finally {
      setIsSubmitting(false);
    }
  }, []);
  const handleLookupBank = useCallback((routingNumber) => getClient().lookupBank(routingNumber), []);
  const handleSaveMethod = useCallback(async (submitted) => {
    setSaveMethodError(null);
    setIsSavingMethod(true);
    try {
      setSavedMethod(await getClient().savePaymentMethod(toProfileRequest(submitted)));
    } catch (error) {
      setSaveMethodError(
        error?.statusCode === 401 ? SESSION_EXPIRED_MESSAGE : readValidationMessage(error, SAVE_METHOD_FAILED_MESSAGE)
      );
    } finally {
      setIsSavingMethod(false);
    }
  }, []);
  if (loadError) return /* @__PURE__ */ jsx(PaymentErrorView, { message: loadError });
  if (receipt) return /* @__PURE__ */ jsx(PaymentReceiptView, { customer, payment: receipt });
  if (savedMethod)
    return /* @__PURE__ */ jsx(PaymentMethodSavedView, { paymentMethod: savedMethod, onDone: endSession });
  if (!balance) return /* @__PURE__ */ jsx(PaymentLoadingView, {});
  return /* @__PURE__ */ jsx(
    PaymentView,
    {
      customer,
      totalBalanceDue: balance.totalBalanceDue,
      orders: balance.orders ?? [],
      isSubmitting,
      submitError,
      isSavingMethod,
      saveMethodError,
      onSubmit: handleSubmit,
      onSaveMethod: handleSaveMethod,
      onLookupBank: handleLookupBank
    }
  );
};
const PaymentRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(PaymentController, {}) }) });
const statusClient = new PaymentPlanStatusTypesClient(CollectionsConfig.current?.api?.host ?? "");
const statusOptions = async () => {
  const statuses = await statusClient.getAll();
  return statuses.map((status) => ({ value: status.id, label: status.statusTypeName }));
};
const createPaymentPlanFilters = ({ customerId } = {}) => new Filters([
  new FilterSelect({ name: "statusId", title: "Status", loader: statusOptions }),
  // Matches the column, which shows the name the view joins in. A plain input, so it searches with
  // the default Contains — `CustomerName ? 'smith'` — and the agent can narrow it to Starts with or
  // Equals from the filter itself.
  //
  // Deliberately not `FilterNumericInput` on the id, which is what this was: that class converts its
  // value with `parseInt`, so an empty box becomes 0 rather than blank, and 0 is a real value that
  // keeps the filter applied. Worse, its `searchType` never reaches the base class, so it searched
  // with Contains — a LIKE against a BIGINT, which the server rejects outright.
  // Scoped to one customer (a customer's plans tab), the customer is a hidden constant instead: the
  // list cannot be widened past that account, and a name search would be noise.
  customerId != null ? new FilterConstant({
    name: "customerId",
    title: "Customer",
    defaultValue: Number(customerId)
  }) : new FilterInput({ name: "customerName", title: "Customer" }),
  new FilterInput({ name: "scheduleDescription", title: "Schedule" }),
  new FilterInput({ name: "description", title: "Description" }),
  new FilterDateRange({ name: "nextRunsOnUtc", title: "Next Payment" })
]);
createPaymentPlanFilters();
const sorting = new Sorting(
  [
    // Follows the column, which now shows the name: the grid matches a sortable column's id against
    // these values, so leaving `customerId` here would make the Customer header do nothing.
    { value: "customerName", title: "Customer" },
    { value: "statusTypeName", title: "Status" },
    { value: "installmentAmount", title: "Installment" },
    { value: "amountRemaining", title: "Remaining" },
    { value: "nextRunsOnUtc", title: "Next Payment" },
    { value: "createdOnUtc", title: "Created" }
  ],
  "createdOnUtc",
  "desc"
);
const paymentPlanStatusColor = (statusId) => {
  switch (statusId) {
    case PaymentPlanStatus.Active:
      return "success";
    case PaymentPlanStatus.Delinquent:
      return "danger";
    case PaymentPlanStatus.AtRisk:
      return "warning";
    case PaymentPlanStatus.PendingFirstPayment:
      return "accent";
    default:
      return "default";
  }
};
const paymentsText = (count) => `${count} ${count === 1 ? "payment" : "payments"}`;
const paymentPlanProgress = (plan) => {
  const count = plan?.installmentCount ?? 0;
  const installment = plan?.installmentAmount ?? 0;
  if (!(count > 0) || !(installment > 0)) return null;
  const total = installment * count;
  const collected = Math.max(plan.amountPaid ?? 0, 0);
  const payments = Math.max(plan.totalPayments ?? 0, 0);
  const label = isPaymentPlanSettled(plan) ? `Paid off · ${paymentsText(payments)}` : `${paymentsText(payments)} · ${formatCurrency(collected)} of ${formatCurrency(total)}`;
  return { total, collected, payments, label };
};
const paymentPlanNextCharge = (plan, formatDate) => {
  if (isPaymentPlanSettled(plan)) return "Paid off";
  if (isPaymentPlanTerminal(plan.statusId) || !plan.nextRunsOnUtc) return "Not scheduled";
  return formatDate(plan.nextRunsOnUtc);
};
const paymentPlanEntryNote = (entry) => entry?.amount < 0 ? `This refund took ${formatCurrency(Math.abs(entry.amount))} off what the plan has collected.` : null;
const paymentPlanTerms = (plan) => [
  `${plan.installmentCount} installments of ${formatCurrency(plan.installmentAmount)}`,
  plan.scheduleDescription
].filter(Boolean).join(" · ");
const paymentPlanTitle = (plan) => [`Payment plan #${plan.id}`, plan.customerName].filter(Boolean).join(" · ");
const PaymentPlanProgress = ({ plan, className = "w-full" }) => {
  const progress = paymentPlanProgress(plan);
  if (!progress) return "—";
  const { total, collected, label } = progress;
  return /* @__PURE__ */ jsxs(
    ProgressBar,
    {
      value: Math.min(collected, total),
      maxValue: total,
      valueLabel: label,
      size: "sm",
      color: collected >= total ? "success" : "accent",
      className,
      children: [
        /* @__PURE__ */ jsx(ProgressBar.Track, { children: /* @__PURE__ */ jsx(ProgressBar.Fill, {}) }),
        /* @__PURE__ */ jsx(ProgressBar.Output, { className: "text-muted text-xs", children: label })
      ]
    }
  );
};
const columnDefs = [
  { propertyName: "customerName", name: "Customer", isSortable: true, minWidth: 180 },
  { propertyName: "statusTypeName", name: "Status", isSortable: true, minWidth: 160 },
  { propertyName: "scheduleDescription", name: "Schedule", isSortable: false, minWidth: 200 },
  { propertyName: "installmentAmount", name: "Installment", isSortable: true, minWidth: 130 },
  // Not sortable, as before: the bar is built in the browser from two columns, so there is nothing
  // for MQL to order the page by.
  { propertyName: "installmentCount", name: "Progress", isSortable: false, minWidth: 170 },
  { propertyName: "amountRemaining", name: "Remaining", isSortable: true, minWidth: 130 },
  { propertyName: "nextRunsOnUtc", name: "Next Payment", isSortable: true, minWidth: 140 }
];
const PaymentPlansTable$1 = ({ plans = [], onView, onCreate, showCustomer = true }) => {
  const runtime = useRuntimeProvider();
  const canCreate = runtime.user.hasPolicy(runtime.config.api.apiKey, "paymentplans", "create");
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      // The account's name, joined in by the view. Null when it has none, and the account number is
      // what this column showed before — so it stays as the fallback rather than an em dash, which
      // would leave the row without any way to tell whose plan it is.
      case "customerName":
        return cellValue || `#${row.customerId}`;
      case "statusTypeName":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: paymentPlanStatusColor(row.statusId), size: "sm", children: cellValue ?? "—" });
      case "installmentAmount":
      case "amountRemaining":
        return formatCurrency(cellValue);
      // Progress: the payments collected (the server's `totalPayments`, the detail's "Successful
      // payments") and the money against the plan's total, which is what the bar fills by.
      //
      // Never the count against the installments, and never installments derived from the money. A
      // payment taken by hand can cover more or less than one installment, so "N of M" reads wrong
      // either way: plan #89, with a $50 payment on $20 installments, read "4 of 12" off the money
      // while the detail counted 3 payments.
      case "installmentCount":
        return /* @__PURE__ */ jsx(PaymentPlanProgress, { plan: row });
      // A UTC string, so it goes through the shared formatter rather than being rendered raw. Null
      // once the plan is terminal, which is an ordinary answer and not a gap in the data.
      case "nextRunsOnUtc":
        return cellValue ? parseAndFormatDate(cellValue) : "—";
      default:
        return cellValue ?? "";
    }
  }, []);
  const columns = useMemo(() => {
    const visible = showCustomer ? columnDefs : columnDefs.filter((column) => column.propertyName !== "customerName");
    return [
      ...visible.map((column, index) => ({
        id: column.propertyName,
        header: column.name,
        // The first visible column names the row: the customer, or the status when that is hidden.
        isRowHeader: index === 0,
        allowsSorting: column.isSortable,
        allowsResizing: true,
        minWidth: column.minWidth,
        cell: (row) => renderCell(row, column.propertyName)
      })),
      // Pinned actions column, carrying nothing but the chevron: editing and removing a plan are
      // out of scope, and cancelling it is an update to its status rather than a row action.
      //
      // The chevron does not do anything the row press below does not already do — it is there to
      // show that the row is pressable, which nothing else on the grid says. Same affordance, and
      // same icon, as the customers list.
      {
        id: "actions",
        header: "",
        pinned: "end",
        width: 64,
        minWidth: 64,
        align: "center",
        cell: (row) => /* @__PURE__ */ jsx(
          Button,
          {
            isIconOnly: true,
            size: "sm",
            variant: "light",
            onPress: () => onView?.(row),
            "aria-label": "View details",
            className: "text-[var(--fg-3)] hover:text-[var(--foreground)]",
            children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-right-s-line" })
          }
        )
      }
    ];
  }, [renderCell, onView, showCustomer]);
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Payment plans list table",
      hideChips: true,
      columns,
      data: plans,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      createLabel: "Set Up Plan",
      onCreate,
      createEnabled: canCreate,
      onRowAction: (key) => {
        const selected = plans.find((plan) => String(plan.id) === String(key));
        if (selected) onView?.(selected);
      },
      topContent: /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-4", children: [
        /* @__PURE__ */ jsx(HorizontalFilters, { scope: "custom", classNames: { base: "grow-1" } }),
        /* @__PURE__ */ jsx(HorizontalChips, { scope: "global" })
      ] })
    }
  );
};
const PaymentPlanListController = reactive.component(
  ({ data, monitors, services, customerId, onView, onCreate }) => {
    const navigate = useNavigate();
    const { paymentPlans } = data;
    const actionKey = paymentPlansPageKey(services.paymentPlans.queryMql, customerId);
    const filters = useMemo(() => createPaymentPlanFilters({ customerId }), [customerId]);
    const handleExecute = useCallback(
      (action, metadata) => action(metadata, actionKey),
      [actionKey]
    );
    const handleView = useCallback(
      (plan) => onView ? onView(plan) : navigate(RoutePaths().paymentPlans.details(plan.id)),
      [navigate, onView]
    );
    const handleCreate = useCallback(
      () => onCreate ? onCreate() : navigate(RoutePaths().paymentPlans.setup()),
      [navigate, onCreate]
    );
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey,
        action: services.paymentPlans.queryMql,
        filters,
        sorting,
        paging: new Paging(customerId != null ? 25 : 50),
        isExecuting: monitors.queryMql.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(
          PaymentPlansTable$1,
          {
            plans: paymentPlans,
            showCustomer: customerId == null,
            onView: handleView,
            onCreate: handleCreate
          }
        )
      },
      actionKey
    );
  },
  {
    name: "PaymentPlanListController",
    queries: ({ customerId }, services) => [
      {
        name: "paymentPlans",
        execute: (db2) => {
          const pageKey = paymentPlansPageKey(services.paymentPlans.queryMql, customerId);
          return db2.withRequestBroker(
            pageKey,
            () => db2.paymentPlansPages.where("pageKey").equals(pageKey),
            { sorting: true }
          );
        },
        deps: [customerId]
      }
    ],
    monitors: (services) => [services.paymentPlans.queryMql]
  }
);
const PaymentPlanList = () => /* @__PURE__ */ jsxs("div", { className: "flex h-full w-full flex-col gap-4", children: [
  /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Collections" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Payment Plans" })
  ] }),
  /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-3", children: /* @__PURE__ */ jsx(PaymentPlanListController, {}) }) })
] });
const MAX_SCHEDULE_DESCRIPTION = 200;
const MAX_DESCRIPTION = 500;
const PLAN_FREQUENCIES = [ScheduleFrequency.Day, ScheduleFrequency.Week, ScheduleFrequency.Month];
const EMPTY_SCHEDULE = { request: null, isValid: false, description: "", firstRun: null };
const CaptureMode = {
  Installment: "installment",
  Total: "total"
};
const CAPTURE_MODE_BUTTONS = [
  {
    key: CaptureMode.Installment,
    label: "Installment amount",
    activeIcon: "ri-checkbox-circle-fill"
  },
  { key: CaptureMode.Total, label: "Plan total", activeIcon: "ri-checkbox-circle-fill" }
];
const AMOUNT_FIELD = {
  [CaptureMode.Installment]: {
    label: "Installment amount",
    required: "Enter the installment amount"
  },
  [CaptureMode.Total]: { label: "Plan total", required: "Enter the plan total" }
};
const CUSTOMER_SEARCH_FIELD = "accounts.customer-search-field";
const PAYMENT_PROFILES_SELECT_FIELD$1 = "accounts.payment-profiles-field";
const wholeNumber = (value) => {
  const parsed = Number(String(value ?? "").trim());
  return Number.isInteger(parsed) ? parsed : null;
};
const toAmountInput = (cents) => (cents / 100).toFixed(2);
const resolvePlanFigures = (mode, amountInput, countInput) => {
  const amount = parseCurrency(amountInput);
  const count = wholeNumber(countInput);
  if (amount === null || count === null || count < 1) return null;
  const typedCents = toCents(amount);
  const installmentCents = mode === CaptureMode.Total ? Math.round(typedCents / count) : typedCents;
  const totalCents = installmentCents * count;
  return {
    count,
    typedCents,
    installmentCents,
    totalCents,
    // Zero unless a typed total had to be rounded to reach a per-instalment amount.
    deltaCents: mode === CaptureMode.Total ? totalCents - typedCents : 0
  };
};
const SetupPaymentPlan = reactive.component(
  ({ services, monitors, customerId: fixedCustomerId, embedded = false, onCreated, onCancel }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const CustomerSearchField = useComponent(CUSTOMER_SEARCH_FIELD);
    const PaymentProfilesSelectField = useComponent(PAYMENT_PROFILES_SELECT_FIELD$1);
    const [mode, setMode] = useState(CaptureMode.Installment);
    const [amountSeed, setAmountSeed] = useState("");
    const [schedule, setSchedule] = useState(EMPTY_SCHEDULE);
    const isSaving = monitors.createPlan.executing;
    const handleScheduleChange = useCallback((request, meta) => {
      setSchedule({
        request,
        isValid: meta.isValid,
        description: meta.description ?? "",
        firstRun: meta.nextRuns?.[0] ?? null
      });
    }, []);
    const formRef = useRef(null);
    const modeRef = useRef(CaptureMode.Installment);
    const handleFormChange = useCallback((next) => {
      formRef.current = next;
      setForm(next);
    }, []);
    const handleModeChange = useCallback((next) => {
      if (next === modeRef.current) return;
      const figures2 = resolvePlanFigures(
        modeRef.current,
        formRef.current?.data?.amount,
        formRef.current?.data?.installmentCount
      );
      modeRef.current = next;
      setAmountSeed(
        figures2 ? toAmountInput(
          next === CaptureMode.Total ? figures2.totalCents : figures2.installmentCents
        ) : ""
      );
      setMode(next);
    }, []);
    const isDescriptionTooLong = schedule.description.length > MAX_SCHEDULE_DESCRIPTION;
    const hasFirstRun = Boolean(schedule.firstRun);
    const isScheduleUsable = schedule.isValid && hasFirstRun && !isDescriptionTooLong;
    useSuccessReactor(
      services.paymentPlans.createPlan,
      (plan) => onCreated ? onCreated(plan) : navigate(RoutePaths().paymentPlans.details(plan.id))
    );
    const cancel = () => onCancel ? onCancel() : navigate(RoutePaths().paymentPlans.list());
    const hasFixedCustomer = fixedCustomerId != null;
    const selectedCustomerId = hasFixedCustomer ? wholeNumber(fixedCustomerId) : wholeNumber(form?.data?.customerId);
    const hasCustomer = (selectedCustomerId ?? 0) > 0;
    const figures = resolvePlanFigures(mode, form?.data?.amount, form?.data?.installmentCount);
    const isAmountUsable = (figures?.installmentCents ?? 0) > 0;
    const canSubmit = form?.isValid === true && isScheduleUsable && isAmountUsable && !isSaving;
    const handleSubmit = () => {
      if (!canSubmit) return;
      const data = form.data;
      services.paymentPlans.createPlan({
        customerId: selectedCustomerId,
        paymentProfileId: wholeNumber(data.paymentProfileId),
        // Provisional. The API needs a sentence at insert time — the plan row is written before the
        // schedule exists — and then replaces this with the one Scheduling derives, which is
        // authoritative. The response is what to trust.
        scheduleDescription: schedule.description,
        // Resolved from whichever two figures were typed. Always the per-instalment amount, never
        // a total: that is the only one of the three the plan stores.
        installmentAmount: figures.installmentCents / 100,
        installmentCount: figures.count,
        description: String(data.description ?? "").trim() || null,
        // The recurrence, exactly as the builder emitted it. The API adds `eventKey`, `resourceType`
        // and `resourceId` — the three things the builder cannot know — and hands it to Scheduling.
        //
        // No `nextRunsOnUtc`: it is derived server-side from this. Sending the date computed here
        // would be asking the browser to settle a day money moves on, and its arithmetic is only good
        // enough for the preview below.
        schedule: schedule.request
      });
    };
    const content = /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(Uniformly, { onChange: handleFormChange, className: "flex flex-col gap-6", children: [
        /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", children: [
          /* @__PURE__ */ jsx("span", { className: "text-muted text-xs font-semibold tracking-wide uppercase", children: "Who is paying" }),
          /* @__PURE__ */ jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [
            !hasFixedCustomer && /* @__PURE__ */ jsx(
              CustomerSearchField,
              {
                name: "customerId",
                label: "Customer account",
                isRequired: true,
                validation: (v) => v.number().required("Select the customer account")
              }
            ),
            hasCustomer ? /* @__PURE__ */ jsx(
              PaymentProfilesSelectField,
              {
                accountId: selectedCustomerId,
                name: "paymentProfileId",
                label: "Payment profile",
                description: "The stored card or bank account each installment is charged to.",
                isRequired: true,
                validation: (v) => v.number().required("Select the payment profile")
              },
              selectedCustomerId
            ) : (
              // The empty state, and only that — the form is already unsubmittable while
              // no customer is chosen, since that field is required too. It stands here
              // so the second column is not empty until a customer is picked, and so the
              // agent can see the field exists and what unlocks it.
              //
              // The accounts field cannot do this job itself: its `sync` fires on mount
              // with whatever `accountId` it was given, so mounting it with none asks the
              // API for the profiles of NaN and raises an error toast.
              /* @__PURE__ */ jsx(
                SelectField,
                {
                  name: "paymentProfileId",
                  label: "Payment profile",
                  description: "Choose a customer account first.",
                  type: "number",
                  isRequired: true,
                  isDisabled: true,
                  items: [],
                  validation: (v) => v.number().required("Select the payment profile"),
                  children: () => null
                }
              )
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx(Separator, {}),
        /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", children: [
          /* @__PURE__ */ jsx("span", { className: "text-muted text-xs font-semibold tracking-wide uppercase", children: "The installments" }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
            /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Enter the plan as" }),
            /* @__PURE__ */ jsx(
              ToggleButtons,
              {
                initialValue: mode,
                buttons: CAPTURE_MODE_BUTTONS,
                onChange: handleModeChange
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [
            /* @__PURE__ */ jsx(
              TextField,
              {
                name: "amount",
                label: AMOUNT_FIELD[mode].label,
                startContent: "$",
                placeholder: "0.00",
                inputMode: "decimal",
                isRequired: true,
                value: amountSeed,
                validation: (v) => v.string().required(AMOUNT_FIELD[mode].required).test("positive-amount", "Enter an amount greater than zero", (value) => {
                  const parsed = parseCurrency(value);
                  return parsed !== null && toCents(parsed) > 0;
                })
              },
              mode
            ),
            /* @__PURE__ */ jsx(
              TextField,
              {
                name: "installmentCount",
                label: "Number of installments",
                inputMode: "numeric",
                isRequired: true,
                value: "",
                validation: (v) => v.string().required("Enter how many installments").test(
                  "at-least-one",
                  "There must be at least one installment",
                  (value) => (wholeNumber(value) ?? 0) >= 1
                )
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "text-muted text-sm", children: [
            mode === CaptureMode.Installment ? "Plan total: " : "Installment amount: ",
            /* @__PURE__ */ jsx("span", { className: "font-medium", children: figures ? formatCurrency(
              (mode === CaptureMode.Installment ? figures.totalCents : figures.installmentCents) / 100
            ) : "—" })
          ] }),
          mode === CaptureMode.Total && figures && !isAmountUsable && /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
            /* @__PURE__ */ jsx(Alert.Indicator, {}),
            /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsxs(Alert.Title, { children: [
              formatCurrency(figures.typedCents / 100),
              " across ",
              figures.count,
              " installments rounds to nothing per installment. Raise the total or lower the count."
            ] }) })
          ] }),
          figures && isAmountUsable && figures.deltaCents !== 0 && /* @__PURE__ */ jsxs(Alert, { status: "warning", children: [
            /* @__PURE__ */ jsx(Alert.Indicator, {}),
            /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsxs(Alert.Title, { children: [
              figures.count,
              " × ",
              formatCurrency(figures.installmentCents / 100),
              " =",
              " ",
              formatCurrency(figures.totalCents / 100),
              ", which is",
              " ",
              formatCurrency(Math.abs(figures.deltaCents) / 100),
              " ",
              figures.deltaCents < 0 ? "less" : "more",
              " than the total entered. This plan collects the figure above; adjust the total or the number of installments to cover it exactly."
            ] }) })
          ] }),
          /* @__PURE__ */ jsx(
            TextAreaField,
            {
              name: "description",
              label: "Notes",
              value: "",
              validation: (v) => v.string().max(MAX_DESCRIPTION, `Cannot exceed ${MAX_DESCRIPTION} characters`)
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", children: [
        /* @__PURE__ */ jsx("span", { className: "text-muted text-xs font-semibold tracking-wide uppercase", children: "When it runs" }),
        /* @__PURE__ */ jsx(
          ScheduleBuilder,
          {
            frequencies: PLAN_FREQUENCIES,
            showMissedRunPolicy: false,
            showEndDate: false,
            previewCount: 3,
            isDisabled: isSaving,
            onChange: handleScheduleChange
          }
        ),
        isDescriptionTooLong && /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
          /* @__PURE__ */ jsx(Alert.Indicator, {}),
          /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsxs(Alert.Title, { children: [
            "This schedule describes itself in more than ",
            MAX_SCHEDULE_DESCRIPTION,
            " characters, which the plan cannot store. Simplify it."
          ] }) })
        ] }),
        schedule.isValid && !hasFirstRun && /* @__PURE__ */ jsxs(Alert, { status: "warning", children: [
          /* @__PURE__ */ jsx(Alert.Indicator, {}),
          /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsx(Alert.Title, { children: "This schedule has no upcoming run, so the plan would start with nothing due. Check the start and end dates." }) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs(DrawerActions, { children: [
        /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", isDisabled: isSaving, onPress: cancel, children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            size: "sm",
            variant: "primary",
            isPending: isSaving,
            isDisabled: !canSubmit,
            onPress: handleSubmit,
            children: "Set Up Plan"
          }
        )
      ] })
    ] });
    if (embedded) return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-6", children: content });
    return /* @__PURE__ */ jsxs("div", { className: "flex h-full w-full flex-col gap-4", children: [
      /* @__PURE__ */ jsx("div", { className: "flex items-center justify-between", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Collections" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().paymentPlans.list()), children: "Payment Plans" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Set Up" })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "flex flex-col gap-6 p-6", children: content }) })
    ] });
  },
  {
    name: "SetupPaymentPlan",
    monitors: (services) => [services.paymentPlans.createPlan]
  }
);
const PAYMENT_PROFILES_SELECT_FIELD = "accounts.payment-profiles-field";
const ChangePaymentProfileModal = ({ plan, isOpen, isSaving, onSave, onClose }) => {
  const PaymentProfilesSelectField = useComponent(PAYMENT_PROFILES_SELECT_FIELD);
  const [form, setForm] = useState({});
  const selected = form.data?.paymentProfileId;
  const canSave = form.isValid && selected != null && selected !== plan.paymentProfileId && !isSaving;
  return /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen, onOpenChange: (open) => !open && onClose(), children: /* @__PURE__ */ jsx(Modal.Container, { size: "sm", placement: "center", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
    /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Heading, { children: "Change payment profile" }) }),
    /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsx(Uniformly, { onChange: setForm, className: "flex flex-col gap-3", children: /* @__PURE__ */ jsx(
      PaymentProfilesSelectField,
      {
        accountId: plan.customerId,
        name: "paymentProfileId",
        label: "Payment profile",
        value: plan.paymentProfileId,
        description: "Future installments are charged to this card or bank account. Payments already taken do not move.",
        isRequired: true,
        validation: (v) => v.number().required("Select the payment profile")
      }
    ) }) }),
    /* @__PURE__ */ jsxs(Modal.Footer, { className: "flex justify-end gap-2", children: [
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", onPress: onClose, children: "Cancel" }),
      /* @__PURE__ */ jsx(
        Button,
        {
          size: "sm",
          variant: "primary",
          isDisabled: !canSave,
          isPending: isSaving,
          onPress: () => onSave(selected),
          children: "Save"
        }
      )
    ] })
  ] }) }) });
};
const ACCOUNT_LINK = "accounts.account-link";
const PAYMENT_DETAIL_DRAWER = "billing.payment-detail-drawer";
const REFUND_PAYMENT_TYPE = 3;
const Detail = ({ label, children }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
  /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: label }),
  /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children })
] });
const ledgerColumns = [
  { key: "createdOnUtc", label: "Created Date" },
  { key: "paymentTypeName", label: "Payment Type" },
  { key: "statusTypeName", label: "Payment Status" },
  { key: "amount", label: "Amount", align: "end" }
];
const paymentStatusColor = (statusTypeId) => {
  switch (statusTypeId) {
    case PaymentStatusType.Paid:
      return "success";
    case PaymentStatusType.Refunded:
    case PaymentStatusType.PartiallyRefunded:
    case PaymentStatusType.Voided:
      return "warning";
    default:
      return isPaymentFailed(statusTypeId) ? "danger" : "default";
  }
};
const renderLedgerCell = (entry, columnKey) => {
  switch (columnKey) {
    // When the charge was attempted, which is the order the ledger reads in. Not `completedOnUtc`:
    // a pending or declined row has none, and the history is of attempts rather than of settlements.
    case "createdOnUtc":
      return parseAndFormatDate(entry.createdOnUtc);
    case "paymentTypeName":
      return entry.paymentTypeName ?? "—";
    // Null when Billing's row is gone, which the view allows for so the entry itself still shows.
    case "statusTypeName":
      return entry.statusTypeId ? /* @__PURE__ */ jsx(Chip, { variant: "soft", size: "sm", color: paymentStatusColor(entry.statusTypeId), children: entry.statusTypeName }) : "—";
    // What the entry collected, signed: a refund reads negative, and a pending or declined attempt
    // reads $0.00 beside a status that says which. The column sums to the plan's Collected figure.
    case "amount":
      return /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: formatCurrency(entry.amount) });
    default:
      return entry[columnKey] ?? "";
  }
};
const PaymentPlanDetailController = reactive.component(
  ({ data, monitors, services, planId, hideCustomer = false }) => {
    const { plan, payments } = data;
    const runtime = useRuntimeProvider();
    const AccountLink = useComponent(ACCOUNT_LINK);
    const PaymentDetailDrawer = useComponent(PAYMENT_DETAIL_DRAWER);
    const [openPayment, setOpenPayment] = useState(null);
    const canCancel = runtime.user.hasPolicy(runtime.config.api.apiKey, "paymentplans", "cancel");
    const canUpdate = runtime.user.hasPolicy(runtime.config.api.apiKey, "paymentplans", "update");
    const [isChangingProfile, setIsChangingProfile] = useState(false);
    useSuccessReactor(
      services.paymentPlans.cancelPlan,
      () => runtime?.services?.alerts?.success?.("The payment plan was canceled.")
    );
    useSuccessReactor(services.paymentPlans.changePaymentProfile, () => {
      setIsChangingProfile(false);
      runtime?.services?.alerts?.success?.("The payment profile was changed.");
    });
    if (!plan?.id) {
      return /* @__PURE__ */ jsx("div", { className: "flex justify-center py-8", children: /* @__PURE__ */ jsx(Spinner, { "aria-label": "Loading the plan" }) });
    }
    const settled = isPaymentPlanSettled(plan);
    const total = plan.installmentAmount * plan.installmentCount;
    const isLoadingLedger = monitors.getByPaymentPlan.executing && payments.length === 0;
    const isCancelling = monitors.cancelPlan.executing;
    const canOfferCancel = canCancel && !isPaymentPlanTerminal(plan.statusId);
    const canOfferProfileChange = canUpdate && !isPaymentPlanTerminal(plan.statusId);
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-6", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-start justify-between gap-4", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
            /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold", children: hideCustomer ? `Payment plan #${plan.id}` : plan.customerName || /* @__PURE__ */ jsxs(Fragment, { children: [
              "Account #",
              /* @__PURE__ */ jsx(AccountLink, { accountId: plan.customerId, accountType: "customer" })
            ] }) }),
            /* @__PURE__ */ jsx(Chip, { variant: "soft", size: "sm", color: paymentPlanStatusColor(plan.statusId), children: plan.statusTypeName ?? "—" })
          ] }),
          !hideCustomer && plan.customerName && /* @__PURE__ */ jsxs("p", { className: "text-muted text-xs", children: [
            "Account #",
            /* @__PURE__ */ jsx(AccountLink, { accountId: plan.customerId, accountType: "customer" })
          ] }),
          /* @__PURE__ */ jsx("p", { className: "text-muted text-xs", children: plan.scheduleDescription })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-0.5", children: [
            /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Remaining" }),
            /* @__PURE__ */ jsx("span", { className: "text-2xl font-semibold tabular-nums", children: formatCurrency(plan.amountRemaining) })
          ] }),
          canOfferCancel && /* @__PURE__ */ jsx(
            ConfirmableButton,
            {
              size: "sm",
              variant: "danger-soft",
              confirmColor: "danger",
              label: "Cancel Payment Plan",
              iconClass: "ri-close-circle-line",
              isLoading: isCancelling,
              isDisabled: isCancelling,
              body: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1 text-sm", children: [
                /* @__PURE__ */ jsxs("span", { children: [
                  "This stops every future installment. ",
                  formatCurrency(plan.amountRemaining),
                  " ",
                  "will no longer be collected through this plan."
                ] }),
                /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "A canceled plan cannot be reopened." })
              ] }),
              onPress: () => services.paymentPlans.cancelPlan(plan.id)
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "border-separator grid gap-4 border-t pt-4 sm:grid-cols-3", children: [
        /* @__PURE__ */ jsx(Detail, { label: "Installment", children: formatCurrency(plan.installmentAmount) }),
        /* @__PURE__ */ jsx(Detail, { label: "Installments", children: plan.installmentCount }),
        /* @__PURE__ */ jsx(Detail, { label: "Plan total", children: formatCurrency(total) }),
        /* @__PURE__ */ jsx(Detail, { label: "Collected", children: formatCurrency(plan.amountPaid) }),
        /* @__PURE__ */ jsx(Detail, { label: "Successful payments", children: plan.totalPayments }),
        /* @__PURE__ */ jsx(Detail, { label: "Paid off", children: settled ? "Yes" : "No" }),
        /* @__PURE__ */ jsx(Detail, { label: "Payment profile", children: /* @__PURE__ */ jsxs("span", { className: "flex flex-wrap items-center gap-2", children: [
          plan.paymentProfileName || `#${plan.paymentProfileId}`,
          canOfferProfileChange && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", onPress: () => setIsChangingProfile(true), children: "Change" })
        ] }) }),
        /* @__PURE__ */ jsx(Detail, { label: "Next installment", children: plan.nextRunsOnUtc ? parseAndFormatDate(plan.nextRunsOnUtc) : "Not scheduled" }),
        /* @__PURE__ */ jsx(Detail, { label: "Created", children: parseAndFormatDate(plan.createdOnUtc) }),
        plan.modifiedOnUtc && /* @__PURE__ */ jsxs(Detail, { label: "Last changed", children: [
          parseAndFormatDateTime(plan.modifiedOnUtc),
          " · ",
          plan.modifiedBy
        ] })
      ] }),
      isChangingProfile && /* @__PURE__ */ jsx(
        ChangePaymentProfileModal,
        {
          plan,
          isOpen: true,
          isSaving: monitors.changePaymentProfile.executing,
          onSave: (profileId) => services.paymentPlans.changePaymentProfile(plan.id, profileId),
          onClose: () => setIsChangingProfile(false)
        }
      ),
      /* @__PURE__ */ jsx(
        PaymentDetailDrawer,
        {
          isOpen: openPayment != null,
          paymentId: openPayment?.paymentId ?? null,
          customerId: plan.customerId,
          entryType: openPayment?.paymentTypeId === REFUND_PAYMENT_TYPE ? "Refund" : "Payment",
          onClose: () => setOpenPayment(null),
          onChanged: () => {
            services.paymentPlans.getById(plan.id);
            services.paymentPlanPayments.getByPaymentPlan(plan.id);
          }
        }
      ),
      plan.description && /* @__PURE__ */ jsxs("div", { className: "border-separator flex flex-col gap-1 border-t pt-4", children: [
        /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Notes" }),
        /* @__PURE__ */ jsx("p", { className: "text-sm whitespace-pre-wrap", children: plan.description })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "border-separator flex flex-col gap-2 border-t pt-4", children: [
        /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Payments" }),
        isLoadingLedger ? /* @__PURE__ */ jsx("div", { className: "flex justify-center py-6", children: /* @__PURE__ */ jsx(Spinner, { size: "sm", "aria-label": "Loading the payments" }) }) : payments.length === 0 ? (
          // The ordinary state of a plan pending its first payment, so it is said plainly rather
          // than left as an empty grid that could read as a load that failed.
          /* @__PURE__ */ jsx("p", { className: "text-muted py-2 text-sm", children: "No payments yet." })
        ) : /* @__PURE__ */ jsx(Table, { className: "w-full", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(
          Table.Content,
          {
            "aria-label": "Payment plan payments",
            onRowAction: (key) => {
              const entry = payments.find((p) => String(p.id) === String(key));
              if (entry?.paymentId) setOpenPayment(entry);
            },
            children: [
              /* @__PURE__ */ jsx(Table.Header, { columns: ledgerColumns, children: (column) => /* @__PURE__ */ jsx(
                Table.Column,
                {
                  id: column.key,
                  align: column.align,
                  isRowHeader: column.key === "createdOnUtc",
                  children: column.label
                },
                column.key
              ) }),
              /* @__PURE__ */ jsx(Table.Body, { items: payments, children: (entry) => /* @__PURE__ */ jsx(Table.Row, { id: entry.id, className: "cursor-pointer", children: ledgerColumns.map((column) => /* @__PURE__ */ jsx(Table.Cell, { align: column.align, children: renderLedgerCell(entry, column.key) }, `${entry.id}-${column.key}`)) }, entry.id) })
            ]
          }
        ) }) })
      ] })
    ] });
  },
  {
    name: "PaymentPlanDetailController",
    sync: (services, { planId }) => [
      {
        execute: () => Number.isFinite(planId) ? services.paymentPlans.getById(planId) : Promise.resolve(),
        deps: [planId]
      },
      {
        execute: () => Number.isFinite(planId) ? services.paymentPlanPayments.getByPaymentPlan(planId) : Promise.resolve(),
        deps: [planId]
      }
    ],
    queries: ({ planId }) => [
      {
        name: "plan",
        defaultValue: null,
        execute: (db2) => Number.isFinite(planId) ? db2.paymentPlans.get(planId) : null,
        deps: [planId]
      },
      {
        name: "payments",
        defaultValue: [],
        // Oldest first, as the API answers: the order the charges happened in, which is what makes
        // Collected readable as a running total down the column.
        execute: (db2) => Number.isFinite(planId) ? db2.paymentPlanPayments.where("paymentPlanId").equals(planId).sortBy("id") : [],
        deps: [planId]
      }
    ],
    monitors: (services) => [
      services.paymentPlans.getById,
      services.paymentPlans.cancelPlan,
      services.paymentPlans.changePaymentProfile,
      services.paymentPlanPayments.getByPaymentPlan
    ]
  }
);
const RecentPaymentPlan = reactive.component(
  ({ data: { plan } }) => {
    useRecentView({
      title: plan?.id ? paymentPlanTitle(plan) : void 0,
      route: plan?.id ? RoutePaths().paymentPlans.details(plan.id) : void 0
    });
    return null;
  },
  {
    name: "RecentPaymentPlan",
    queries: ({ planId }) => [
      {
        name: "plan",
        defaultValue: null,
        execute: (db2) => Number.isFinite(planId) ? db2.paymentPlans.get(planId) : null,
        deps: [planId]
      }
    ]
  }
);
const ViewPaymentPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const planId = Number(id);
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full w-full flex-col gap-4", children: [
    /* @__PURE__ */ jsx(RecentPaymentPlan, { planId }),
    /* @__PURE__ */ jsx("div", { className: "flex items-center justify-between", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Collections" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().paymentPlans.list()), children: "Payment Plans" }),
      /* @__PURE__ */ jsxs(BreadcrumbsItem, { children: [
        "#",
        planId
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-6", children: /* @__PURE__ */ jsx(PaymentPlanDetailController, { planId }) }) })
  ] });
};
const PaymentPlansRoutes = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/setup",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "paymentplans", operation: "create", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(SetupPaymentPlan, {}) })
    }
  ),
  /* @__PURE__ */ jsx(Route, { path: "/:id/details", element: /* @__PURE__ */ jsx(ViewPaymentPlan, {}) }),
  /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(PaymentPlanList, {}) })
] });
const Home = () => {
  const hasPaySession2 = useHasPaySession();
  return /* @__PURE__ */ jsx(
    Navigate,
    {
      to: hasPaySession2 ? RoutePaths().payment.root() : RoutePaths().paymentPlans.list(),
      replace: true
    }
  );
};
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "payment/*", element: /* @__PURE__ */ jsx(PaymentRoutes, {}) }),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "payment-plans/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "paymentplans", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(PaymentPlansRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(Home, {}) })
] }) });
function getCollectionsNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "Make a Payment",
        route: "/payment",
        icon: "ri-bank-card-line",
        isVisible: hasPaySession()
      },
      {
        title: "Payment Plans",
        route: "/payment-plans",
        icon: "ri-calendar-schedule-line",
        isVisible: runtime.user.hasPolicy(apiKey, "paymentplans", "view")
      }
    ]
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const PlanDetailDrawer = withDrawerController(
  ({ item }) => item ? /* @__PURE__ */ jsx("div", { className: "p-6", children: /* @__PURE__ */ jsx(PaymentPlanDetailController, { planId: Number(item.planId), hideCustomer: true }) }) : null
);
const PlanSetupDrawer = withDrawerController(
  ({ item, onClose, onSuccess }) => item ? /* @__PURE__ */ jsx(
    SetupPaymentPlan,
    {
      embedded: true,
      customerId: item.customerId,
      onCreated: onSuccess,
      onCancel: onClose
    }
  ) : null
);
const CustomerPaymentPlans = ({ accountId }) => {
  const customerId = Number(accountId);
  const [listVersion, setListVersion] = useState(0);
  const refresh = () => setListVersion((v) => v + 1);
  const [detail, DetailDrawer] = useDrawerController(PlanDetailDrawer, {
    isOpen: false,
    size: "3xl",
    onClose: refresh
  });
  const [setup, SetupDrawer] = useDrawerController(PlanSetupDrawer, {
    isOpen: false,
    size: "3xl",
    headerContent: () => "Set up payment plan",
    // The plan setup just created, so the agent sees what was arranged.
    onSuccess: (plan) => plan?.id && detail.open({ planId: plan.id })
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      PaymentPlanListController,
      {
        customerId,
        onView: (plan) => detail.open({ planId: plan.id }),
        onCreate: () => setup.open({ customerId })
      },
      listVersion
    ),
    /* @__PURE__ */ jsx(DetailDrawer, {}),
    /* @__PURE__ */ jsx(SetupDrawer, {})
  ] });
};
const PAYMENT_PLANS_TABLE = "collections.payment-plans-table";
const PaymentPlansTableBody = ({ accountId }) => accountId != null ? createElement(CustomerPaymentPlans, { accountId }) : createElement(PaymentPlanListController, {});
const PaymentPlansTable = (props) => createElement(
  Embed,
  null,
  createElement(PaymentPlansTableBody, props)
);
const Figure = ({ label, children }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
  /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: label }),
  /* @__PURE__ */ jsx("span", { className: "text-sm font-medium tabular-nums", children })
] });
const PaymentPlanNoticeBody = reactive.component(
  ({ services, paymentId, className }) => {
    const id = Number(paymentId);
    const [entry, setEntry] = useState(null);
    const [plan, setPlan] = useState(null);
    const planIdRef = useRef(null);
    useSuccessReactor(services.paymentPlanPayments.getByPayment, (entries, args) => {
      if (Number(args[0]) !== id) return;
      const found = entries?.[0] ?? null;
      setEntry(found);
      if (!found) return;
      planIdRef.current = Number(found.paymentPlanId);
      services.paymentPlans.getById(planIdRef.current);
    });
    useSuccessReactor(services.paymentPlans.getById, (record, args) => {
      if (Number(args[0]) === planIdRef.current) setPlan(record);
    });
    if (!entry || !plan) return null;
    const note = paymentPlanEntryNote(entry);
    return /* @__PURE__ */ jsxs(Alert, { status: "accent", className, children: [
      /* @__PURE__ */ jsx(Alert.Indicator, { children: /* @__PURE__ */ jsx("i", { className: "ri-calendar-schedule-line text-base" }) }),
      /* @__PURE__ */ jsxs(Alert.Content, { className: "gap-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
            /* @__PURE__ */ jsxs(Alert.Title, { children: [
              "Part of payment plan #",
              plan.id
            ] }),
            /* @__PURE__ */ jsx(Chip, { variant: "soft", size: "sm", color: paymentPlanStatusColor(plan.statusId), children: plan.statusTypeName ?? "—" })
          ] }),
          /* @__PURE__ */ jsx(
            Link,
            {
              to: RoutePaths().paymentPlans.details(plan.id),
              className: "text-accent text-xs font-medium hover:underline",
              children: "View plan"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs(Alert.Description, { className: "flex flex-col gap-3", children: [
          /* @__PURE__ */ jsx("span", { children: paymentPlanTerms(plan) }),
          /* @__PURE__ */ jsx(PaymentPlanProgress, { plan }),
          /* @__PURE__ */ jsxs("span", { className: "grid grid-cols-3 gap-3", children: [
            /* @__PURE__ */ jsx(Figure, { label: "Collected", children: formatCurrency(plan.amountPaid) }),
            /* @__PURE__ */ jsx(Figure, { label: "Remaining", children: formatCurrency(plan.amountRemaining) }),
            /* @__PURE__ */ jsx(Figure, { label: "Next installment", children: paymentPlanNextCharge(plan, parseAndFormatDate) })
          ] }),
          note && /* @__PURE__ */ jsx("span", { children: note })
        ] })
      ] })
    ] });
  },
  {
    name: "PaymentPlanNotice",
    sync: (services, { paymentId }) => [
      {
        execute: () => Number(paymentId) > 0 ? services.paymentPlanPayments.getByPayment(Number(paymentId)) : Promise.resolve(),
        deps: [paymentId]
      }
    ]
  }
);
const PaymentPlanNotice$1 = ({ paymentId, className }) => {
  const runtime = useRuntimeProvider();
  const canView = runtime.user.hasPolicy(runtime.config.api.apiKey, "paymentplans", "view");
  if (!canView || paymentId == null) return null;
  return /* @__PURE__ */ jsx(PaymentPlanNoticeBody, { paymentId, className });
};
const PAYMENT_PLAN_NOTICE = "collections.payment-plan-notice";
const PaymentPlanNotice = (props) => createElement(
  Embed,
  null,
  createElement(PaymentPlanNotice$1, props)
);
const NO_PAYMENT_PLAN = 0;
const findPaymentPlan = (plans, id) => id ? (plans ?? []).find((plan) => Number(plan.id) === Number(id)) : void 0;
const paymentPlanOptionTitle = (plan) => `Payment plan #${plan.id}`;
const paymentPlanOptionDetail = (plan) => [
  plan.statusTypeName,
  `${formatCurrency(plan.amountRemaining)} remaining`,
  paymentPlanTerms(plan)
].filter(Boolean).join(" · ");
const paymentPlanAmountError = (plan, amount) => plan && Number(amount) > Number(plan.amountRemaining) ? `More than the ${formatCurrency(plan.amountRemaining)} payment plan #${plan.id} still owes.` : null;
const NONE_ITEM = { id: NO_PAYMENT_PLAN };
const PaymentPlanSelectFieldBody = reactive.component(
  ({ services, customerId, onPlansLoaded, ...rest }) => {
    const [plans, setPlans] = useState([]);
    useSuccessReactor(services.paymentPlans.getOpenByCustomer, (payload, args) => {
      if (Number(args[0]) !== Number(customerId)) return;
      const loaded = payload ?? [];
      setPlans(loaded);
      onPlansLoaded?.(loaded);
    });
    if (plans.length === 0) return null;
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        label: "Payment plan",
        placeholder: "Not part of a payment plan",
        ...rest,
        type: "number",
        items: [NONE_ITEM, ...plans],
        renderValue: (plan) => plan.id === NO_PAYMENT_PLAN ? "Not part of a payment plan" : paymentPlanOptionTitle(plan),
        children: (plan) => plan.id === NO_PAYMENT_PLAN ? /* @__PURE__ */ jsxs(ListBox.Item, { id: plan.id, textValue: "Not part of a payment plan", children: [
          "Not part of a payment plan",
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, plan.id) : /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: plan.id,
            textValue: `${paymentPlanOptionTitle(plan)}, ${paymentPlanOptionDetail(plan)}`,
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col", children: [
                /* @__PURE__ */ jsx(Label, { children: paymentPlanOptionTitle(plan) }),
                /* @__PURE__ */ jsx(Description, { children: paymentPlanOptionDetail(plan) })
              ] }),
              /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
            ]
          },
          plan.id
        )
      }
    );
  },
  {
    name: "PaymentPlanSelectField",
    sync: (services, { customerId }) => [
      {
        execute: () => Number(customerId) > 0 ? services.paymentPlans.getOpenByCustomer(Number(customerId)) : Promise.resolve(),
        deps: [customerId]
      }
    ]
  }
);
const useCanLinkPaymentPlans = () => {
  const runtime = useRuntimeProvider();
  const apiKey = runtime.config.api.apiKey;
  return runtime.user.hasPolicy(apiKey, "paymentplans", "view") && runtime.user.hasPolicy(apiKey, "paymentplans", "update");
};
const PaymentPlanSelectField$1 = (props) => {
  const canLink = useCanLinkPaymentPlans();
  if (!canLink || props.customerId == null) return null;
  return /* @__PURE__ */ jsx(PaymentPlanSelectFieldBody, { ...props });
};
const PAYMENT_PLAN_SELECT_FIELD = "collections.payment-plan-field";
const PaymentPlanSelectField = (props) => createElement(
  Embed,
  null,
  createElement(PaymentPlanSelectField$1, props)
);
const PaymentPlanLinkBody = reactive.component(
  ({ services, monitors, paymentId, customerId, amount, onLinked, className }) => {
    const id = Number(paymentId);
    const [linked, setLinked] = useState(null);
    const [plans, setPlans] = useState([]);
    const [form, setForm] = useState({ data: {} });
    useSuccessReactor(services.paymentPlanPayments.getByPayment, (entries, args) => {
      if (Number(args[0]) === id) setLinked((entries ?? []).length > 0);
    });
    useSuccessReactor(services.paymentPlanPayments.link, (_entry, args) => {
      if (Number(args[0]?.paymentId) !== id) return;
      services.paymentPlanPayments.getByPayment(id);
      onLinked?.();
    });
    if (linked !== false) return null;
    const plan = findPaymentPlan(plans, form.data.paymentPlanId);
    const amountError = paymentPlanAmountError(plan, amount);
    const hasPlans = plans.length > 0;
    return /* @__PURE__ */ jsxs(
      Uniformly,
      {
        className: hasPlans ? `flex flex-col gap-3 rounded-lg border p-3 ${className ?? ""}` : "",
        onChange: setForm,
        children: [
          hasPlans && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
            /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Add to a payment plan" }),
            /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "The plan counts this payment toward what it still owes. Scheduled charges keep their dates; the plan finishes sooner." })
          ] }),
          /* @__PURE__ */ jsx(
            PaymentPlanSelectField$1,
            {
              customerId,
              name: "paymentPlanId",
              onPlansLoaded: setPlans
            }
          ),
          amountError && /* @__PURE__ */ jsx("p", { className: "text-danger text-xs", children: amountError }),
          hasPlans && /* @__PURE__ */ jsx("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx(
            SaveButton,
            {
              label: "Add to payment plan",
              iconClass: "ri-calendar-check-line",
              withConfirm: true,
              isDisabled: !plan || Boolean(amountError),
              isLoading: monitors.link.executing,
              body: plan && /* @__PURE__ */ jsxs("div", { children: [
                "This adds payment ",
                /* @__PURE__ */ jsxs("strong", { children: [
                  "#",
                  id
                ] }),
                " for",
                " ",
                /* @__PURE__ */ jsx("strong", { children: formatCurrency(Number(amount)) }),
                " to payment plan",
                " ",
                /* @__PURE__ */ jsxs("strong", { children: [
                  "#",
                  plan.id
                ] }),
                ". It cannot be taken off the plan afterwards."
              ] }),
              onPress: () => services.paymentPlanPayments.link({ paymentPlanId: plan.id, paymentId: id })
            }
          ) })
        ]
      }
    );
  },
  {
    name: "PaymentPlanLink",
    sync: (services, { paymentId }) => [
      {
        execute: () => Number(paymentId) > 0 ? services.paymentPlanPayments.getByPayment(Number(paymentId)) : Promise.resolve(),
        deps: [paymentId]
      }
    ],
    monitors: (services) => [services.paymentPlanPayments.link]
  }
);
const PaymentPlanLink$1 = (props) => {
  const canLink = useCanLinkPaymentPlans();
  if (!canLink || props.paymentId == null || props.customerId == null) return null;
  return /* @__PURE__ */ jsx(PaymentPlanLinkBody, { ...props });
};
const PAYMENT_PLAN_LINK = "collections.payment-plan-link";
const PaymentPlanLink = (props) => createElement(
  Embed,
  null,
  createElement(PaymentPlanLink$1, props)
);
let appRuntime = null;
const initCollectionsRuntime = (hostRuntime, { baseUrl = "/collections" } = {}) => {
  if (!CollectionsConfig.current)
    console.warn(
      `[collections] No configuration entry matches ${globalThis.location?.origin}. Add this host's origin to apps/collections/src/core/config.js; until then every API call goes to a relative URL.`
    );
  appRuntime = hostRuntime.registerApp("collections", {
    baseUrl,
    description: {
      title: "Collections",
      iconClass: "ri-bank-card-fill",
      defaultRoute: "/payment-plans",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getCollectionsNavigation,
    config: () => CollectionsConfig.current
  });
  hostRuntime.components.register(PAYMENT_PLANS_TABLE, PaymentPlansTable);
  hostRuntime.components.register(PAYMENT_PLAN_NOTICE, PaymentPlanNotice);
  hostRuntime.components.register(PAYMENT_PLAN_SELECT_FIELD, PaymentPlanSelectField);
  hostRuntime.components.register(PAYMENT_PLAN_LINK, PaymentPlanLink);
};
export {
  CollectionsClient,
  PaySessionProvider,
  initCollectionsRuntime,
  usePaySession
};
//# sourceMappingURL=index.js.map
